import {combineBookingQuote} from "../../workers/api-gateway/src/mvp/booking-quote";
import { NEWS } from "./content/news";
import { latestRequestId } from "./api";
import { ShareTools,InstallHint,SalonPage,MyBooking } from "./ShareTools";
import { PlanPanel,RecoveryCode,RecoveryPage,RecoverySettings,SupportPanel,BookingReport,DataTools,HelpPage,NewsPage,PromoPanel } from "./ServiceSupport";
import { promoEligible,type Promo } from "../../workers/api-gateway/src/mvp/promo-core";
import { InquiryForm,InquiryAnswer,DesignerInbox,ReviewReplies } from "./portal/InquiryPanel";
import { CustomerReschedule,CustomerReviewEditor,DesignerBookingChanges } from "./CustomerBookingTools";
import { ContactQueue } from "./portal/ContactQueue";
import { NextClient, OperationsPanel } from "./portal/OperationsPanel";
import { BookingPassAction } from "./portal/ClientExtensions";
import { BookingRulesPanel } from "./portal/BookingRulesPanel";
import { SwitchPage } from "./SwitchPage";
import { StartSteps } from "./portal/StartSteps";
import { PortalShell, usePortalTab } from "./portal/PortalShell";
import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import QRCode from "qrcode";
import {
  ArrowRight,
  BarChart3,
  BellRing,
  CalendarClock,
  CalendarDays,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  Heart,
  LogOut,
  Menu,
  MessageCircle,
  Plus,
  Scissors,
  Search,
  Share2,
  ShieldCheck,
  Star,
  Users,
  X,
} from "lucide-react";
import {
  API_URL,
  ApiError,
  api,
  errorMessage,
  track,
} from "./api";
import { BrandMark, BrandWordmark } from "./Brand";
import { Turnstile } from "./Turnstile";
import {
  AccountPage,
  AdminPage,
  DashboardPlatformExtras,
  MatchingPage,
} from "./Platform";
/** 서버가 주는 시술 메뉴 프리셋. 정본: apps/workers/api-gateway/src/mvp/content/service-presets.ts */
interface ServicePreset {
  id: string;
  category: string;
  name: string;
  durationMinutes: number;
  rebookingWeeks: number;
  consultationQuestion: string;
}

import { downloadBookingCalendar } from "./booking-calendar";
import { openStateOf } from "./booking-open-state";
import {
  normalizePublicBookingData,
  type PublicData,
} from "./public-booking-data";
import {
  bookingPrivacyControls,
  type BookingPrivacy,
  type BookingPrivacyBusy,
} from "./booking-privacy";
import { BusinessWorkspace } from "./BusinessWorkspace";
import {
  bookingFeedbackEnabled,
  playBookingFeedback,
  setBookingFeedbackEnabled,
} from "./booking-feedback";
import {
  loadingOAuthProviders,
  oauthProviderSurface,
  resolveOAuthProviders,
  type OAuthProvider,
  type OAuthProviderState,
} from "./auth-provider-state";

const locale = window.location.pathname.split("/")[1] === "en" ? "en" : "ko";

function appPath(): string {
  const stripped = window.location.pathname.replace(/^\/(ko|en)(?=\/|$)/, "");
  return stripped || "/";
}

function go(path: string): void {
  const localized = path === "/" ? `/${locale}` : `/${locale}${path}`;
  window.history.pushState({}, "", localized);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0, behavior: "smooth" });
}

// sitemap이 선언한 공개 라우트마다 실제로 다른 제목과 설명을 준다.
// 같은 제목·같은 canonical은 "이 페이지들은 사실 같은 문서"라는 선언이다.
const STATIC_PAGE_META: Record<string, { title: string; description: string }> = {
  "/": {
    title: "HairBridge — 예약 가능한 개인 헤어 디자이너 찾기",
    description:
      "지역, 시술과 날짜로 실제 작업·가격·빈 시간을 비교하고 가입 없이 예약하세요. 개인 디자이너는 자기 이름의 예약과 고객 관계를 이어갑니다.",
  },
  "/start": {
    title: "무료로 예약 링크 만들기 | HairBridge",
    description:
      "카드 등록 없이 가입해 프로필·지역·전문 시술·서비스·운영시간을 채우고 내 이름의 예약 링크를 만듭니다.",
  },
  "/pricing": {
    title: "기능과 요금 | HairBridge",
    description:
      "공개 예약 페이지와 고객 장부는 무료입니다. 자동 갱신과 카드 등록 없이 시작하고 필요할 때만 선택합니다.",
  },
  "/privacy": {
    title: "개인정보처리방침 | HairBridge",
    description:
      "HairBridge가 수집하는 예약·고객 정보의 항목, 보관 기간, 파기 절차와 정보주체의 권리를 안내합니다.",
  },
  "/terms": {
    title: "이용약관 | HairBridge",
    description:
      "HairBridge 서비스 이용 조건, 예약과 취소, 디자이너와 고객의 책임 범위를 안내합니다.",
  },
  "/support": {
    title: "고객지원 | HairBridge",
    description:
      "예약·계정·데이터 삭제 문의 창구와 응답 기준, 서비스 상태 확인 방법을 안내합니다.",
  },
  "/discover": {
    title: "예약 가능한 개인 헤어 디자이너 찾기 | HairBridge",
    description:
      "지역, 원하는 시술과 날짜를 입력해 실제 가능한 시간, 작업, 가격과 완료 예약 후기를 함께 비교하세요.",
  },
};

function App() {
  const [path, setPath] = useState(appPath());
  const [online,setOnline]=useState(navigator.onLine);
  useEffect(()=>{const up=()=>setOnline(true);const down=()=>setOnline(false);window.addEventListener("online",up);window.addEventListener("offline",down);return()=>{window.removeEventListener("online",up);window.removeEventListener("offline",down);};},[]);
  useEffect(()=>{try{const first=localStorage.getItem("hb_first_visit");if(!first){localStorage.setItem("hb_first_visit",String(Date.now()));return;}const days=Math.floor((Date.now()-Number(first))/86400000);if(days>=7&&!localStorage.getItem("hb_return_recorded")){track("return_visit",{role:path==="/start"?"designer":"customer",daysBucket:days<14?"7-13":days<30?"14-29":"30+"});localStorage.setItem("hb_return_recorded","1");}}catch{/* storage unavailable: do not fabricate a return clock */}},[path]);
  useEffect(() => {
    const update = () => setPath(appPath());
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);

  let page: React.ReactNode;
  if (path === "/") page = <Landing />;
  else if (path === "/discover") page = <MatchingPage />;
  else if (path === "/account") page = <AccountPage />;
  else if (path === "/admin") page = <AdminPage />;
  else if (path === "/switch") page = <SwitchPage />;
  else if (path === "/start") page = <DesignerEntry />;
  else if (path === "/pricing") page = <Pricing />;
  else if (path === "/privacy") page = <Policy kind="privacy" />;
  else if (path === "/terms") page = <Policy kind="terms" />;
  else if (path === "/support") page = <Support />;
  else if(path.startsWith("/salon/"))page=<SalonPage region={decodeURIComponent(path.slice("/salon/".length))}/>;
  else if(path === "/my-booking")page=<MyBooking/>;
  else if (path === "/recover") page=<RecoveryPage/>;
  else if (path === "/help" || path.startsWith("/help/")) page=<HelpPage/>;
  else if (path === "/news") page=<NewsPage/>;
  else if (path.startsWith("/inquiry/")) page=<InquiryAnswer token={decodeURIComponent(path.slice("/inquiry/".length))}/>;
  else if (path.startsWith("/waitlist/"))
    page = (
      <ManageWaitlist
        token={decodeURIComponent(path.slice("/waitlist/".length))}
      />
    );
  else if (path.startsWith("/book/"))
    page = (
      <PublicBooking slug={decodeURIComponent(path.slice("/book/".length))} />
    );
  else if (path.startsWith("/designer/"))
    page = (
      <PublicBooking
        slug={decodeURIComponent(path.slice("/designer/".length))}
      />
    );
  else if (path.startsWith("/manage/"))
    page = (
      <ManageBooking
        token={decodeURIComponent(path.slice("/manage/".length))}
      />
    );
  else if (path.startsWith("/booking-action/"))
    page = (
      <BookingAction
        token={decodeURIComponent(path.slice("/booking-action/".length))}
      />
    );
  else page = <NotFound />;

  const isPublicBooking =
    path.startsWith("/book/") ||
    path.startsWith("/designer/") ||
    path.startsWith("/waitlist/");
  useEffect(() => {
    // 관리 토큰이 들어간 주소는 색인 대상이 아니다. 고객이 그 링크를 어딘가에
    // 붙여넣으면 예약자 이름·연락처가 담긴 화면이 검색 결과에 남을 수 있다.
    // 공개적으로 유통되어야 하는 것은 디자이너의 /designer/:slug와 하위 호환 /book/:slug뿐이다.
    const secretRoute = ["/manage/", "/waitlist/", "/booking-action/"].some(
      (prefix) => path.startsWith(prefix),
    );
    const known =
      !secretRoute &&
      (["/", "/start", "/discover", "/pricing", "/privacy", "/terms", "/support"].includes(path) ||
        path.startsWith("/book/") ||
        path.startsWith("/designer/"));
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    robots.content = known ? "index,follow" : "noindex,nofollow";
  }, [path]);

  // sitemap의 공개 URL별로 index.html의 canonical과 title을 갱신한다.
  // 모든 라우트에서 /ko 하나로 고정돼 있었다. 그러면 검색엔진은 요금·약관·
  // 개인정보·고객지원을 홈의 중복으로 접어버리고, sitemap이 선언한 페이지는
  // 색인에 남지 않는다. 라우트마다 canonical·title·description을 갱신한다.
  // 예약 페이지(/book/:slug)는 PublicBooking이 디자이너 이름으로 따로 덮는다.
  useEffect(() => {
    if (isPublicBooking) return;
    const meta = STATIC_PAGE_META[path];
    if (!meta) return;
    const canonical = `${window.location.origin}/${locale}${path === "/" ? "" : path}`;
    document.title = meta.title;
    const set = (selector: string, value: string) => {
      const element = document.querySelector<HTMLMetaElement>(selector);
      if (element) element.content = value;
    };
    const link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (link) link.href = canonical;
    set('meta[name="description"]', meta.description);
    set('meta[property="og:title"]', meta.title);
    set('meta[property="og:description"]', meta.description);
    set('meta[property="og:url"]', canonical);
  }, [path, isPublicBooking]);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">본문으로 건너뛰기</a>
      {!online&&<div className="offline-banner" role="alert">인터넷 연결이 끊겼어요. 저장되지 않았어요. 입력한 내용을 두고 연결 후 다시 저장해 주세요.</div>}
      <Header compact={isPublicBooking} landing={path === "/"} />
      <div id="main-content" tabIndex={-1}>{page}</div>
      <Footer compact={isPublicBooking} landing={path === "/"} />
    </div>
  );
}

function Header({
  compact = false,
  landing = false,
}: {
  compact?: boolean;
  landing?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const currentPath = appPath();
  let unreadNews=false;try{unreadNews=localStorage.getItem("hb_news_seen")!==(NEWS[0]?.date||"");}catch{}
  return (
    <header className={compact ? "site-header booking-header" : "site-header"}>
      <div className="nav-wrap">
        <button
          className="brand"
          onClick={() => go("/")}
          aria-label="HairBridge 홈"
        >
          <BrandMark />
          <BrandWordmark />
        </button>
        {compact ? (
          <span className="booking-header-note">
            <ShieldCheck /> 가입 없이 안전하게 예약
          </span>
        ) : (
          <>
            <button
              className="mobile-menu"
              onClick={() => setOpen((value) => !value)}
              aria-label="메뉴 열기"
              aria-expanded={open}
              aria-controls="main-navigation"
            >
              {open ? <X /> : <Menu />}
            </button>
            <nav
              id="main-navigation"
              className={open ? "nav-links is-open" : "nav-links"}
              aria-label="주 메뉴"
            >
              <button onClick={()=>{go("/help");setOpen(false);}}>도움말</button><button onClick={()=>{go("/news");setOpen(false);}}>바뀐 점{unreadNews&&currentPath!=="/news"&&<small aria-label="새 소식"> · 새 소식</small>}</button>
              <button
                aria-current={currentPath === "/discover" ? "page" : undefined}
                onClick={() => {
                  go("/discover");
                  setOpen(false);
                }}
              >
                디자이너 찾기
              </button>
              <button
                aria-current={currentPath === "/account" ? "page" : undefined}
                onClick={() => {
                  go("/account");
                  setOpen(false);
                }}
              >
                내 예약
              </button>
              <a
                href={`/${locale}/pricing`}
                aria-current={currentPath === "/pricing" ? "page" : undefined}
              >
                기능과 요금
              </a>
              {!landing && (
                <button
                  className="button button-small"
                  aria-current={currentPath === "/start" ? "page" : undefined}
                  onClick={() => {
                    go("/start");
                    setOpen(false);
                  }}
                >
                  디자이너로 시작
                </button>
              )}
            </nav>
          </>
        )}
      </div>
    </header>
  );
}

function Landing() {
  useEffect(
    () => track("landing_view", { page:"home", source: document.referrer ? "external" : "direct" }),
    [],
  );
  const today = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  const [selectedDate, setSelectedDate] = useState(today);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const selectedDateLabel = new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul", year: "numeric", month: "long", day: "numeric", weekday: "short",
  }).format(new Date(`${selectedDate}T12:00:00+09:00`));
  return (
    <main>
      <section className="landing-desk">
        <div className="landing-desk-copy">
          <h1>
            DM 답장 대신,
            <br />
            예약 링크 하나.
          </h1>
          <p>고객 명부는 언제든 들고 가요. 수수료 없이 내 이름으로 예약받아요.</p>
          <button className="button button-large" onClick={() => go("/start")}>내 예약 링크 만들기 <ArrowRight /></button>
          <button className="text-button" onClick={() => go("/switch")}>쓰던 고객 파일 옮겨 보기</button>
          <details className="landing-customer-search"><summary>손님인가요? 디자이너 찾기</summary>
          <form
            className="landing-discovery-form"
            onSubmit={(event) => {
              event.preventDefault();
              const values = new FormData(event.currentTarget);
              const query = new URLSearchParams({
                region: String(values.get("region") || ""),
                service: String(values.get("service") || ""),
                date: String(values.get("date") || selectedDate),
              });
              go(`/discover?${query}`);
            }}
          >
            <label>
              <span>지역</span>
              <input name="region" placeholder="예: 성수, 합정 (선택)" />
            </label>
            <label>
              <span>원하는 시술</span>
              <input name="service" placeholder="예: 레이어드 커트" required />
            </label>
            <label className="landing-date-field">
              <span>날짜</span>
              <button
                type="button"
                className="landing-date-trigger"
                onClick={() => dateInputRef.current?.showPicker?.()}
                aria-label="방문 날짜 고르기"
              >
                {selectedDateLabel} <CalendarDays aria-hidden="true" />
              </button>
              <input
                ref={dateInputRef}
                className="landing-date-native"
                name="date"
                type="date"
                min={today}
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
                required
              />
            </label>
            <button className="button button-large landing-desk-primary">
              예약 가능한 디자이너 보기 <ArrowRight />
            </button>
          </form></details>
          <div className="landing-role-switch">
            <span>개인 디자이너인가요?</span>
            <button className="text-button" onClick={() => go("/start")}>
              내 이름으로 예약받기
            </button>
          </div>
          <p className="landing-desk-free">
            가입 없이 예약 · 카드 등록 없음
          </p>
        </div>
        <aside className="landing-product-example" aria-label="예약 링크 예시 화면">
          <p>예시 화면 · 실제 예약이 아니에요</p><h2>수아 디자이너의 예약 페이지</h2>
          <div className="chart-service"><b>레이어드 커트</b><span>60분 · 40,000원</span></div>
          <p>공개 가격과 시간을 손님이 직접 확인해요.</p>
          <div className="example-slots"><span>오전 10:00</span><span>오후 2:00</span><span>오후 4:30</span></div>
          <p>내 고객 · 내 예약 · 언제든 파일로 반출</p>
        </aside>
      </section>
    </main>
  );
}

interface AuthResult {
  recoveryCode?:string;
  accessToken: string;
}

function DesignerEntry() {
  const [token, setToken] = useState(
    () => localStorage.getItem("hb_access_token") || "",
  );
  const [oauthMessage, setOauthMessage] = useState("");
  const [oauthBusy, setOauthBusy] = useState(false);
  const authenticated = (nextToken: string) => {
    localStorage.setItem("hb_access_token", nextToken);
    setToken(nextToken);
  };
  const logout = () => {
    localStorage.removeItem("hb_access_token");
    setToken("");
  };
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const code = params.get("oauth_code");
    const oauthError = params.get("oauth_error");
    if (!code && !oauthError) return;
    window.history.replaceState(
      {},
      "",
      window.location.pathname + window.location.search,
    );
    if (oauthError) {
      setOauthMessage(errorMessage(new ApiError(oauthError, 400)));
      return;
    }
    setOauthBusy(true);
    void api<AuthResult>("/api/v1/auth/oauth/exchange", {
      method: "POST",
      body: JSON.stringify({ code }),
    })
      .then((data) => authenticated(data.accessToken))
      .catch((error) => setOauthMessage(errorMessage(error)))
      .finally(() => setOauthBusy(false));
  }, []);
  return (
    <main className="portal-page">
      {oauthBusy ? (
        <Loading label="소셜 로그인을 완료하는 중" />
      ) : token ? (
        <DesignerPortal token={token} onLogout={logout} />
      ) : (
        <AuthPanel
          onAuthenticated={authenticated}
          externalMessage={oauthMessage}
        />
      )}
    </main>
  );
}

function AuthPanel({
  onAuthenticated,
  externalMessage,
}: {
  onAuthenticated: (token: string) => void;
  externalMessage?: string;
}) {
  const [pendingAuth,setPendingAuth]=useState<AuthResult|null>(null);
  const [mode, setMode] = useState<"register" | "login">("register");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileAttempt, setTurnstileAttempt] = useState(0);
  const [providerState, setProviderState] = useState<OAuthProviderState>(
    loadingOAuthProviders,
  );

  const loadProviders = useCallback(() => {
    setProviderState(loadingOAuthProviders());
    void resolveOAuthProviders(() =>
      api<{ google: boolean; naver: boolean }>(
        "/api/v1/auth/oauth/providers",
      ),
    ).then(setProviderState);
  }, []);

  useEffect(loadProviders, [loadProviders]);

  const providerSurface = oauthProviderSurface(providerState);

  function socialLogin(provider: OAuthProvider) {
    window.location.assign(
      `${API_URL}/api/v1/auth/oauth/${provider}/start?locale=${locale}`,
    );
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    const form = new FormData(event.currentTarget);
    try {
      const payload =
        mode === "register"
          ? {
              email: form.get("email"),
              password: form.get("password"),
              name: form.get("name"),
            invite: new URLSearchParams(window.location.search).get("invite") || undefined,
              turnstileToken: turnstileToken || undefined,
            }
          : {
              email: form.get("email"),
              password: form.get("password"),
              turnstileToken: turnstileToken || undefined,
            };
      const data = await api<AuthResult>(`/api/v1/auth/${mode}`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if(data.recoveryCode)setPendingAuth(data);else onAuthenticated(data.accessToken);
    } catch (error) {
      setMessage(errorMessage(error));
      setTurnstileToken("");
      setTurnstileAttempt((value) => value + 1);
    } finally {
      setBusy(false);
    }
  }

  if(pendingAuth?.recoveryCode)return <RecoveryCode code={pendingAuth.recoveryCode} onContinue={()=>onAuthenticated(pendingAuth.accessToken)}/>;
  return (
    <section className="auth-wrap">
      <div className="auth-intro">
        <p className="eyebrow">가입하고 바로 시작</p>
        <h1>
          나만의 예약 페이지를
          <br />
          지금 만들어 보세요.
        </h1>
        <ul>
          <li>
            <Check />
            카드 정보 없이 시작
          </li>
          <li>
            <Check />
            베타 기간 예약 무료
          </li>
          <li>
            <Check />
            언제든 데이터 삭제 요청
          </li>
        </ul>
      </div>
      <form className="panel auth-panel" onSubmit={submit}>
        <div className="segmented">
          <button
            type="button"
            className={mode === "register" ? "active" : ""}
            onClick={() => {
              setMode("register");
              setTurnstileToken("");
              setTurnstileAttempt((value) => value + 1);
            }}
          >
            가입
          </button>
          <button
            type="button"
            className={mode === "login" ? "active" : ""}
            onClick={() => {
              setMode("login");
              setTurnstileToken("");
              setTurnstileAttempt((value) => value + 1);
            }}
          >
            로그인
          </button>
        </div>
        <h2>{mode === "register" ? "무료로 시작하기" : "다시 오셨군요"}</h2>
        {providerSurface.available.length > 0 && (
          <div className="social-login-grid">
            {providerSurface.available.includes("google") && (
              <button
                type="button"
                className="social-login google"
                onClick={() => socialLogin("google")}
              >
                <span>G</span> Google로 계속하기
              </button>
            )}
            {providerSurface.available.includes("naver") && (
              <button
                type="button"
                className="social-login naver"
                onClick={() => socialLogin("naver")}
              >
                <span>N</span>네이버로 계속하기
              </button>
            )}
          </div>
        )}
        {providerSurface.notice && (
          <div
            className={providerState.status === "error" ? "status error" : "form-note"}
            role={providerState.status === "error" ? "alert" : "status"}
          >
            <span>{providerSurface.notice}</span>
            {providerSurface.retry && (
              <button
                type="button"
                className="button button-outline"
                onClick={loadProviders}
              >
                다시 확인
              </button>
            )}
          </div>
        )}
        <div className="auth-divider">
          <span>또는 이메일로</span>
        </div>
        {mode === "register" && (
          <Field
            label="디자이너 이름"
            name="name"
            placeholder="예: 김수아"
            minLength={2}
            required
          />
        )}
        <Field
          label="이메일"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="designer@example.com"
          required
        />
        <Field
          label="비밀번호"
          name="password"
          type="password"
          autoComplete={
            mode === "register" ? "new-password" : "current-password"
          }
          minLength={mode === "register" ? 10 : 1}
          placeholder={mode === "register" ? "10자 이상" : "비밀번호"}
          required
        />
        {mode==="login"&&<button className="text-button" type="button" onClick={()=>go("/recover")}>비밀번호를 잊었어요</button>}
        <Turnstile key={turnstileAttempt} onToken={setTurnstileToken} />
        {(message || externalMessage) && (
          <Status kind="error">{message || externalMessage}</Status>
        )}
        <button className="button button-full" disabled={busy}>
          {busy
            ? "처리 중…"
            : mode === "register"
              ? "내 페이지 만들기"
              : "로그인"}
        </button>
        <p className="form-note">
          계속하면{" "}
          <button type="button" onClick={() => go("/terms")}>
            이용약관
          </button>
          과{" "}
          <button type="button" onClick={() => go("/privacy")}>
            개인정보처리방침
          </button>
          에 동의합니다.
        </p>
      </form>
    </section>
  );
}

interface Profile {
  slug: string;
  salon_name: string | null;
  bio: string | null;
  phone: string | null;
  locale: string;
  timezone: string;
  weekly_schedule: string | Record<string, [string, string]>;
  region_label: string | null;
  salon_address: string | null;
  specialties: string[];
  career_started_year: number | null;
  instagram_handle?: string | null;
  credentials_note?: string | null;
  address_visibility?: string;
  listing_status: string;
  listing_status_reason: string | null;
  is_published: number;
}
interface MeResult {
  user: { id: string; email: string; name: string };
  profile: Profile;
  invitedCount?: number;
  listingQuality: {
    ready: boolean;
    missing: string[];
    completion: number;
  };
}
interface Service {
  id: string;
  name: string;
  category: string;
  description: string | null;
  price: number;
  price_max: number | null;
  duration_minutes: number;
  rebooking_interval_weeks: number;
  price_notes?: string | string[];
  intake_questions:
    | string
    | Array<{ label: string; required: boolean }>;
  is_active: number;
}
interface Booking {
  deposit_status?:string;
  promo_code?:string;promo_discount?:number;
  client_id?:string; style_asset_path?:string; late_minutes?:number; no_show_count?:number; late_count?:number;
  id: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string | null;
  scheduled_at: string;
  status: string;
  service_name: string;
  price: number;
  customer_notes: string | null;
  intake_answers: string;
  acquisition_source: string;
  review_id: string | null;
  review_rating: number | null;
  review_comment: string | null;
  review_status: "published" | "under_review" | "hidden" | null;
  dispute_note: string | null;
  unread_messages: number;
  pending_change_requests: number;
}
interface AnalyticsSummary {
  bookings: {
    total_bookings?: number;
    confirmed_bookings?: number;
    completed_bookings?: number;
    cancelled_bookings?: number;
    no_show_bookings?: number;
    upcoming_week_confirmed?: number;
  };
  reviews: {
    count?: number;
    average?: number | null;
  };
  acquisition: Array<{ source: string; bookings: number; completed: number }>;
  retention: { repeat_clients?: number };
}

const defaultSchedule: Record<string, [string, string]> = {
  1: ["10:00", "18:00"],
  2: ["10:00", "18:00"],
  3: ["10:00", "18:00"],
  4: ["10:00", "18:00"],
  5: ["10:00", "18:00"],
  6: ["10:00", "16:00"],
};

function DesignerPortal({
  token,
  onLogout,
}: {
  token: string;
  onLogout: () => void;
}) {
  const [bookingReasons,setBookingReasons]=useState<Record<string,string>>({});
  const [portalTab, changePortalTab] = usePortalTab();
  const [me, setMe] = useState<MeResult | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [qr, setQr] = useState("");
  const [serviceBusy, setServiceBusy] = useState(false);
  // 시술 메뉴 프리셋 — 가입 직후 빈 폼 앞에서 멈추지 않게 한다.
  // 계약: docs/product/root-keyword-expansion.json CONTENT-SERVICE-PRESET
  const [servicePresets, setServicePresets] = useState<ServicePreset[]>([]);
  const serviceFormRef = useRef<HTMLFormElement | null>(null);
  const [feedbackEnabled, setFeedbackEnabled] = useState(
    bookingFeedbackEnabled,
  );
  const knownBookingIds = useRef<Set<string> | null>(null);
  const initialSetupOpened=useRef(false);

  const applyServicePreset = useCallback((preset: ServicePreset) => {
    const form = serviceFormRef.current;
    if (!form) return;
    const set = (name: string, value: string) => {
      const field = form.elements.namedItem(name);
      if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement) {
        field.value = value;
      }
    };
    set("name", preset.name);
    set("durationMinutes", String(preset.durationMinutes));
    set("rebookingIntervalWeeks", String(preset.rebookingWeeks));
    set("intakeQuestions", preset.consultationQuestion);
    // 가격은 채우지 않는다 — 시술가는 디자이너가 정할 값이고 잘못된 기본값이 공개 페이지에 남는다.
    const priceField = form.elements.namedItem("price");
    if (priceField instanceof HTMLInputElement) priceField.focus();
    track("service_preset_applied", { preset: preset.id });
  }, []);

  useEffect(() => {
    void api<{ servicePresets: ServicePreset[] }>(
      "/api/v1/business/content/presets",
      {},
      token,
    )
      .then((data) => setServicePresets(data.servicePresets ?? []))
      .catch(() => setServicePresets([]));
  }, [token]);

  const load = useCallback(async () => {
    try {
      const [meData, serviceData, bookingData, summaryData] = await Promise.all(
        [
          api<MeResult>("/api/v1/designer/me", {}, token),
          api<Service[]>("/api/v1/designer/services", {}, token),
          api<Booking[]>("/api/v1/designer/bookings", {}, token),
          api<AnalyticsSummary>(
            "/api/v1/designer/analytics/summary",
            {},
            token,
          ),
        ],
      );
      // preview API can still contain profiles created before `specialties` became
      // mandatory. Treat an absent optional list as empty so one legacy profile
      // cannot blank the entire designer workspace before its settings are saved.
      const normalizedMe: MeResult = {
        ...meData,
        profile: {
          ...meData.profile,
          specialties: Array.isArray(meData.profile.specialties)
            ? meData.profile.specialties
            : [],
          weekly_schedule: meData.profile.weekly_schedule ?? defaultSchedule,
        },
      };
      setMe(normalizedMe);
      setServices(serviceData);
      const previous = knownBookingIds.current;
      if (
        previous &&
        bookingData.some((booking) => !previous.has(booking.id))
      ) {
        playBookingFeedback("new_booking", feedbackEnabled);
      }
      knownBookingIds.current = new Set(
        bookingData.map((booking) => booking.id),
      );
      setBookings(bookingData);
      setSummary(summaryData);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) onLogout();
      else setMessage(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [token, onLogout, feedbackEnabled]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(()=>{if(me&&!me.profile.is_published&&!initialSetupOpened.current){initialSetupOpened.current=true;changePortalTab("mypage");}},[me,changePortalTab]);
  const shareUrl = me?.profile.slug
    ? `${window.location.origin}/${locale}/designer/${me.profile.slug}`
    : "";
  useEffect(() => {
    if (shareUrl)
      void QRCode.toDataURL(shareUrl, {
        width: 220,
        margin: 1,
        color: { dark: "#302520", light: "#ffffff" },
      }).then(setQr);
  }, [shareUrl]);

  if (loading) return <Loading label="예약 공간을 불러오는 중" />;
  if (!me)
    return (
      <Status kind="error">{message || "페이지를 불러오지 못했어요."}</Status>
    );
  const total = Number(summary?.bookings.total_bookings || 0);
  // 답을 기다리는 예약. 오래 기다린 순으로 올려 먼저 답하게 한다.
  const pendingBookings = bookings
    .filter((booking) => booking.status === "pending")
    .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
  const schedule =
    typeof me.profile.weekly_schedule === "string"
      ? (() => {
          try {
            return JSON.parse(me.profile.weekly_schedule) as Record<
              string,
              [string, string]
            >;
          } catch {
            return defaultSchedule;
          }
      })()
      : me.profile.weekly_schedule;
  const setupState = [
    Boolean(me.profile.slug && me.profile.salon_name),
    Boolean(me.profile.region_label && me.profile.specialties.length > 0),
    services.length > 0,
    Boolean(Object.keys(schedule).length > 0),
    Boolean(me.profile.is_published && services.length > 0),
  ];
  const setupComplete = setupState.filter(Boolean).length;

  async function saveProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");
    const form = new FormData(event.currentTarget);
    const weeklySchedule: Record<string, [string, string]> = {};
    for (const day of ["1", "2", "3", "4", "5", "6"]) {
      if (form.get(`open-${day}`))
        weeklySchedule[day] = [
          String(form.get(`start-${day}`)),
          String(form.get(`end-${day}`)),
        ];
    }
    try {
      await api(
        "/api/v1/designer/me",
        {
          method: "PUT",
          body: JSON.stringify({
            slug: form.get("slug"),
            salonName: form.get("salonName") || null,
            bio: form.get("bio") || null,
            regionLabel: form.get("regionLabel") || null,
            salonAddress: form.get("salonAddress") || null,
            instagramHandle: form.get("instagramHandle") || null,
            credentialsNote: form.get("credentialsNote") || null,
            addressVisibility: form.get("addressVisibility") || "confirmed_only",
            specialties: String(form.get("specialties") || "")
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean)
              .slice(0, 8),
            careerStartedYear: form.get("careerStartedYear")
              ? Number(form.get("careerStartedYear"))
              : null,
            phone: form.get("phone") || null,
            locale: "ko",
            weeklySchedule,
            isPublished: form.get("published") === "on",
          }),
        },
        token,
      );
      setMessage("예약 페이지를 저장했어요.");
      await load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }

  async function addService(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");
    setServiceBusy(true);
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      await api(
        "/api/v1/designer/services",
        {
          method: "POST",
          body: JSON.stringify({
            name: form.get("name"),
            priceNotes: String(form.get("priceNotes") || "").split("\n").filter(Boolean).slice(0,3),
            category: form.get("category"),
            description: form.get("description") || null,
            price: Number(form.get("price")),
            priceMax: form.get("priceMax")
              ? Number(form.get("priceMax"))
              : null,
            durationMinutes: Number(form.get("durationMinutes")),
            rebookingIntervalWeeks: Number(form.get("rebookingIntervalWeeks")),
            intakeQuestions: String(form.get("intakeQuestions") || "")
              .split("\n")
              .map((label) => label.trim())
              .filter(Boolean)
              .slice(0, 5)
              .map((label) => ({ label, required: true })),
            isActive: true,
          }),
        },
        token,
      );
      formElement.reset();
      setMessage("시술 메뉴를 추가했어요.");
      await load();
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setServiceBusy(false);
    }
  }

  async function changeStatus(
    id: string,
    status: "confirmed" | "completed" | "cancelled" | "no_show",
  ) {
    setMessage("");
    try {
      await api(
        `/api/v1/designer/bookings/${id}/status`,
        { method: "PATCH", body: JSON.stringify({ status,reasonCode:bookingReasons[id] || "other" }) },
        token,
      );
      if (status === "confirmed")
        playBookingFeedback("booking_confirmed", feedbackEnabled);
      if (status === "completed")
        playBookingFeedback("rebooking_ready", feedbackEnabled);
      await load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }

  async function disputeReview(event: FormEvent<HTMLFormElement>, reviewId: string) {
    event.preventDefault();
    setMessage("");
    const form = event.currentTarget;
    try {
      await api(
        `/api/v1/designer/reviews/${reviewId}/dispute`,
        {
          method: "POST",
          body: JSON.stringify({ note: new FormData(form).get("note") }),
        },
        token,
      );
      form.reset();
      setMessage("후기를 비공개 검토로 전환하고 이의 내용을 전달했어요.");
      await load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }

  async function copyLink() {
    await navigator.clipboard.writeText(shareUrl);
    setMessage("예약 링크를 복사했어요.");
  }

  async function copyTrackedLink(source: "instagram" | "kakao" | "naver") {
    await navigator.clipboard.writeText(`${shareUrl}?src=${source}`);
    setMessage(`${source} 어디서 왔는지 표시되는 예약 링크를 복사했어요.`);
  }

  return (
    <section className="dashboard-wrap">
      <div className="dashboard-head">
        <div>
          <p className="eyebrow">내 예약실</p>
          <h1>{me.user.name}님, 반가워요.</h1>
          <p>카드 등록 없이 무료로 쓰고, 고객 명부는 언제든 가져가요.</p>
        </div>
        <button className="button button-outline" onClick={onLogout}>
          <LogOut /> 로그아웃
        </button>
      </div>
      {message && (
        <Status
          kind={
            message.includes("못") || message.includes("확인")
              ? "error"
              : "success"
          }
        >
          {message}
        </Status>
      )}

      {/*
        미용사는 시술 사이 30초에 폰을 꺼낸다. 그때 필요한 것은 지표가 아니라
        "지금 답해야 할 예약"이다. 2026-08-11 실측에서 확정/노쇼 버튼이 있는
        예약 관리 섹션은 화면 8.2개(6,945px) 아래에 있었다. 그 거리면 시술
        사이에 쓸 수 없고, 확인이 밀리면 고객은 답을 못 받는다.
        확인 대기가 있을 때만 최상단에 띄우고 여기서 바로 확정한다.
      */}
      <div hidden={portalTab !== "today"}>
      <NextClient token={token} onOpen={id=>{const url=new URL(window.location.href);url.searchParams.set("client",id);window.history.replaceState({},"",url);changePortalTab("clients");}} />
      {pendingBookings.length > 0 && (
        <section className="today-first" aria-labelledby="today-first-heading">
          <h2 id="today-first-heading">
            답변을 기다리는 예약 {pendingBookings.length}건
          </h2>
          <p>확정하면 고객 화면의 상태가 바로 바뀝니다.</p>
          <ul>
            {pendingBookings.slice(0, 3).map((booking) => (
              <li key={booking.id}>
                <span>
                  <b>{formatDateTime(booking.scheduled_at)}</b>
                  <small>
                    {booking.customer_name} · {booking.service_name}
                  </small>
                </span>
                <span className="today-first-actions">
                  <button
                    onClick={() => void changeStatus(booking.id, "confirmed")}
                  >
                    예약 확정하기
                  </button>
                  <button
                    className="button-outline"
                    onClick={() => void changeStatus(booking.id, "cancelled")}
                  >
                    거절
                  </button>
                </span>
              </li>
            ))}
          </ul>
          {pendingBookings.length > 3 && (
            <button
              className="text-button"
              onClick={() =>
                document
                  .getElementById("booking-manage")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              나머지 {pendingBookings.length - 3}건 보기 <ArrowRight />
            </button>
          )}
        </section>
      )}

      {total > 0 ? <details className="portal-metrics"><summary>예약 기록 보기</summary>
      <div className="metric-grid">
        <article>
          <CalendarDays />
          <span>
            <small>전체 예약</small>
            <b>{total}</b>
          </span>
        </article>
        <article>
          <CheckCircle2 />
          <span>
            <small>이번 주 확정</small>
            <b>{Number(summary?.bookings.upcoming_week_confirmed || 0)}</b>
          </span>
        </article>
        <article>
          <BarChart3 />
          <span>
            <small>완료 예약</small>
            <b>{Number(summary?.bookings.completed_bookings || 0)}</b>
          </span>
        </article>
        <article>
          <Star />
          <span>
            <small>고객 평점</small>
            <b>
              {summary?.reviews.count
                ? `${Number(summary.reviews.average).toFixed(1)} / 5`
                : "–"}
            </b>
          </span>
        </article>
      </div>
      <p className="metric-note">
        예약 기록 기준 · 취소 {Number(summary?.bookings.cancelled_bookings || 0)}건 ·
        노쇼 {Number(summary?.bookings.no_show_bookings || 0)}건. 방지된
        노쇼처럼 확인할 수 없는 값은 표시하지 않아요.
      </p>
      <section className="panel evidence-panel" aria-labelledby="evidence-heading">
        <div className="panel-title">
          <span>
            <h2 id="evidence-heading">어디서 오고 누가 다시 오는지</h2>
            <p>실제 예약 기록에 저장된 값만 보여드려요. 광고 효과를 추정하지 않습니다.</p>
          </span>
          <b>재방문 고객 {Number(summary?.retention?.repeat_clients || 0)}명</b>
        </div>
        <div className="source-breakdown">
          {(summary?.acquisition || []).map((item) => (
            <span key={item.source}>
              <b>{item.source}</b>
              <small>예약 {Number(item.bookings)} · 완료 {Number(item.completed || 0)}</small>
            </span>
          ))}
          {(summary?.acquisition || []).length === 0 && <p>아직 예약이 없어서 보여드릴 게 없어요.</p>}
        </div>
      </section>

      </details> : <section className="panel"><h2>손님에게 예약 링크를 보내요</h2><p>전화·DM으로 시간을 다시 묻지 않아도 돼요.</p><button className="button" onClick={() => changePortalTab("mypage")}>내 링크 보내기</button></section>}
      </div>
      <div hidden={portalTab !== "mypage"}>
      {me.profile.is_published&&<section className="panel"><h2>예약 링크가 열렸어요.</h2><p>지금 손님에게 보내면 가입 없이 예약할 수 있어요.</p><code className="public-link">{shareUrl}</code><button className="button button-full" onClick={copyLink}><Copy/>예약 링크 복사</button></section>}
      {!me.profile.is_published && <StartSteps token={token} profile={me.profile} name={me.user.name} onReady={load} />}
      <section className="setup-ribbon" aria-labelledby="setup-heading">
        <div className="setup-ribbon-head">
          <span>
            <p className="eyebrow">시작하기</p>
            <h2 id="setup-heading">
              {setupComplete === 5
                ? "검색과 예약을 받을 준비가 끝났어요."
                : "빠진 항목을 채우면 검색 결과에 공개할 수 있어요."}
            </h2>
          </span>
          <strong>{setupComplete} / 5 완료</strong>
        </div>
        <ol>
          <li className={setupState[0] ? "is-complete" : ""}>
            <span>{setupState[0] ? <Check /> : "1"}</span>
            <div>
              <b>브랜드 정보</b>
              <small>활동명과 예약 주소 정하기</small>
            </div>
          </li>
          <li className={setupState[1] ? "is-complete" : ""}>
            <span>{setupState[1] ? <Check /> : "2"}</span>
            <div>
              <b>활동 지역과 전문 시술</b>
              <small>이용객이 나를 찾을 조건 정하기</small>
            </div>
          </li>
          <li className={setupState[2] ? "is-complete" : ""}>
            <span>{setupState[2] ? <Check /> : "3"}</span>
            <div>
              <b>서비스 메뉴</b>
              <small>가격과 소요 시간 등록하기</small>
            </div>
          </li>
          <li className={setupState[3] ? "is-complete" : ""}>
            <span>{setupState[3] ? <Check /> : "4"}</span>
            <div>
              <b>실제 가능한 시간</b>
              <small>일하는 요일과 시간을 확인하기</small>
            </div>
          </li>
          <li className={setupState[4] ? "is-complete" : ""}>
            <span>{setupState[4] ? <Check /> : "5"}</span>
            <div>
              <b>개인 프로필 공개</b>
              <small>검색과 공유 링크에 함께 공개하기</small>
            </div>
          </li>
        </ol>
      </section>

      </div>
      <BusinessWorkspace token={token} services={services} onChanged={load} portalTab={portalTab} />
      <div hidden={portalTab !== "mypage"}>

      <DashboardPlatformExtras
        accessToken={token}
        services={services.map(({ id, name }) => ({ id, name }))}
      />

      <div className="dashboard-section-heading" id="booking-page-settings">
        <span>02</span>
        <div>
          <p className="eyebrow">고객에게 보이는 화면</p>
          <h2>브랜드 예약 페이지</h2>
          <p>고객에게 보일 소개와 운영 시간을 한 번만 정리하세요.</p>
        </div>
      </div>
      <div className="dashboard-grid">
        <form className="panel" onSubmit={saveProfile}>
          <div className="panel-title">
            <span>
              <h2>예약 페이지 설정</h2>
              <p>고객에게 보일 정보와 시간을 입력하세요.</p>
            </span>
            <label className="publish-toggle">
              <input
                name="published"
                type="checkbox"
                defaultChecked={Boolean(me.profile.is_published)}
              />
              <span /> 공개
            </label>
          </div>
          <div className="form-grid">
            <Field
              label="예약 주소"
              name="slug"
              defaultValue={me.profile.slug}
              // `-`를 문자 클래스 끝에 두면 브라우저의 `v` 플래그 파서가
              // 예약 문자로 읽어 pattern 전체를 무효화한다. 무효 pattern은
              // 콘솔 오류만 남기고 클라이언트 검증을 통째로 꺼버려서,
              // 잘못된 예약 주소가 서버까지 그대로 간다.
              pattern="[a-z0-9][a-z0-9\-]{2,39}"
              required
            />
            <Field
              label="활동명 또는 살롱명"
              name="salonName"
              defaultValue={me.profile.salon_name || ""}
            />
          </div>
          <div className="form-grid">
            <Field label="인스타 아이디" name="instagramHandle" defaultValue={me.profile.instagram_handle || ""} pattern="[a-zA-Z0-9_.]*" maxLength={30} />
            <Field label="교육·자격 한 줄" name="credentialsNote" defaultValue={me.profile.credentials_note || ""} maxLength={200} />
            <label className="field"><span>주소 공개 범위</span><select name="addressVisibility" defaultValue={me.profile.address_visibility || "confirmed_only"}><option value="confirmed_only">확정 손님에게만 상세 주소</option><option value="area_only">동네만 공개</option><option value="public">상세 주소 공개</option></select></label>
            <Field
              label="활동 지역"
              name="regionLabel"
              defaultValue={me.profile.region_label || ""}
              placeholder="예: 서울 성수"
              required
            />
            <Field
              label="현재 근무지 주소 (선택)"
              name="salonAddress"
              defaultValue={me.profile.salon_address || ""}
              placeholder="공개할 수 있는 범위만 입력"
            />
          </div>
          <div className="form-grid">
            <Field
              label="전문 시술 (쉼표로 구분)"
              name="specialties"
              defaultValue={me.profile.specialties.join(", ")}
              placeholder="레이어드 커트, 탈색, 곱슬 교정"
              required
            />
            <Field
              label="경력 시작 연도 (선택)"
              name="careerStartedYear"
              type="number"
              min={1950}
              max={new Date().getFullYear()}
              defaultValue={me.profile.career_started_year || ""}
            />
          </div>
          <Field
            label="연락처"
            name="phone"
            defaultValue={me.profile.phone || ""}
            placeholder="010-0000-0000"
          />
          <label className="field">
            <span>소개</span>
            <textarea
              name="bio"
              defaultValue={me.profile.bio || ""}
              maxLength={1000}
              rows={3}
              placeholder="고객에게 나를 소개해 주세요."
            />
          </label>
          <fieldset className="schedule">
            <legend>예약 가능 시간</legend>
            {["1", "2", "3", "4", "5", "6"].map((day, index) => (
              <div key={day}>
                <label>
                  <input
                    type="checkbox"
                    name={`open-${day}`}
                    defaultChecked={Boolean(schedule[day])}
                  />
                  {["월", "화", "수", "목", "금", "토"][index]}
                </label>
                <input
                  aria-label={`${day}요일 시작 시간`}
                  name={`start-${day}`}
                  type="time"
                  step="1800"
                  defaultValue={schedule[day]?.[0] || "10:00"}
                />
                <span>–</span>
                <input
                  aria-label={`${day}요일 종료 시간`}
                  name={`end-${day}`}
                  type="time"
                  step="1800"
                  defaultValue={schedule[day]?.[1] || "18:00"}
                />
              </div>
            ))}
          </fieldset>
          <button className="button">설정 저장</button>
        </form>

        <aside className="panel share-panel">
          <div className="panel-title">
            <span>
              <h2>내 예약 링크</h2>
              <p>
                {me.profile.is_published
                  ? "지금 고객에게 공유할 수 있어요."
                  : "공개 스위치를 켜야 고객이 볼 수 있어요."}
              </p>
            </span>
          </div>
          {qr && <img src={qr} alt="예약 페이지 QR 코드" />}
          <code>{shareUrl}</code>
          <div className="button-row">
            <button className="button button-full" onClick={copyLink}>
              <Copy /> 링크 복사
            </button>
            <button
              className="button button-outline"
              aria-label="예약 페이지 새 창에서 열기"
              title="예약 페이지 새 창에서 열기"
              onClick={() => window.open(shareUrl, "_blank")}
            >
              <ExternalLink />
            </button>
          </div>
          <p className="share-tip">
            인스타그램 프로필, 카카오톡 채널, 기존 고객 DM에 붙여 보세요.
          </p>
          <div className="tracked-links" aria-label="어디서 왔는지 표시되는 링크">
            <button onClick={() => void copyTrackedLink("instagram")}>Instagram 링크</button>
            <button onClick={() => void copyTrackedLink("kakao")}>Kakao 링크</button>
            <button onClick={() => void copyTrackedLink("naver")}>Naver 링크</button>
          </div>
        </aside>
      </div>

      <div className="dashboard-section-heading" id="service-menu-settings">
        <span>03</span>
        <div>
          <p className="eyebrow">시술 메뉴</p>
          <h2>서비스와 가격</h2>
          <p>
            고객이 고민하지 않도록 메뉴, 시간, 다음 방문 주기를 함께 보여주세요.
          </p>
        </div>
      </div>
      <div className="dashboard-grid services-grid">
        <form className="panel" onSubmit={addService} ref={serviceFormRef}>
          <div className="panel-title">
            <span>
              <h2>서비스 추가</h2>
              <p>고객이 선택할 메뉴를 만드세요.</p>
            </span>
          </div>
          {servicePresets.length > 0 && (
            <div className="preset-picker">
              <span className="preset-picker-label">
                자주 쓰는 메뉴로 시작하기
                <small>이름·소요시간·재방문 주기·상담 질문이 채워집니다. 가격은 직접 정하세요.</small>
              </span>
              <div className="preset-chips">
                {servicePresets.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    className="preset-chip"
                    onClick={() => applyServicePreset(preset)}
                  >
                    {preset.name}
                    <small>{preset.durationMinutes}분</small>
                  </button>
                ))}
              </div>
            </div>
          )}
          <Field
            label="서비스명"
            name="name"
            placeholder="예: 디자인 커트"
            minLength={2}
            required
          />
          <label className="field">
            <span>시술 분류</span>
            <select name="category" defaultValue="cut">
              <option value="cut">커트</option>
              <option value="color">컬러</option>
              <option value="perm">펌</option>
              <option value="care">클리닉·케어</option>
              <option value="styling">스타일링</option>
              <option value="barber">바버</option>
              <option value="other">기타</option>
            </select>
          </label>
          <Field
            label="설명"
            name="description"
            placeholder="예: 샴푸와 스타일링 포함"
          />
          <div className="form-grid">
            <Field
              label="가격(원)"
              name="price"
              type="number"
              min={0}
              step={1000}
              defaultValue="55000"
              required
            />
            <Field
              label="최대 가격(선택)"
              name="priceMax"
              type="number"
              min={0}
              step={1000}
              placeholder="길이·모량에 따른 상한"
            />
            <label className="field">
              <span>소요 시간</span>
              <select name="durationMinutes" defaultValue="60">
                <option value="30">30분</option>
                <option value="60">60분</option>
                <option value="90">90분</option>
                <option value="120">120분</option>
                <option value="180">180분</option>
              </select>
            </label>
            <label className="field">
              <span>다음 방문 후보</span>
              <select name="rebookingIntervalWeeks" defaultValue="6">
                <option value="4">4주 후</option>
                <option value="5">5주 후</option>
                <option value="6">6주 후</option>
                <option value="7">7주 후</option>
                <option value="8">8주 후</option>
              </select>
            </label>
          </div>
          <label className="field">
            <span>예약 전 상담 질문 (선택, 한 줄에 하나·최대 5개)</span>
            <textarea
              name="intakeQuestions"
              rows={4}
              placeholder={"원하는 길이 또는 스타일은 무엇인가요?\n최근 시술 이력이 있나요?"}
            />
            <small>민감한 건강정보를 필수로 요구하지 마세요.</small>
          </label>
          <label className="field"><span>추가금 조건 (3줄까지)</span><textarea name="priceNotes" rows={3} maxLength={300} /></label>
          <button className="button" disabled={serviceBusy}>
            <Plus /> {serviceBusy ? "추가 중…" : "서비스 추가"}
          </button>
        </form>
        <div className="panel">
          <div className="panel-title">
            <span>
              <h2>서비스 목록</h2>
              <p>{services.length}개를 등록했어요.</p>
            </span>
          </div>
          {services.length === 0 ? (
            <Empty
              icon={<Scissors />}
              title="아직 서비스가 없어요"
              text="시술 메뉴를 하나 추가하면 예약을 받을 수 있어요."
            />
          ) : (
            <div className="service-list">
              {services.map((service) => (
                <div key={service.id}>
                  <span>
                    <b>{service.name}</b>
                    <small>
                      {service.duration_minutes}분 ·{" "}
                      {service.description || "설명 없음"} ·{" "}
                      {service.rebooking_interval_weeks}주 후보
                      {(() => {
                        try {
                          const questions = Array.isArray(service.intake_questions)
                            ? service.intake_questions
                            : JSON.parse(service.intake_questions || "[]");
                          return questions.length ? ` · 상담 질문 ${questions.length}개` : "";
                        } catch {
                          return "";
                        }
                      })()}
                    </small>
                  </span>
                  <details><summary>메뉴 수정·삭제</summary><form onSubmit={async event=>{event.preventDefault();const f=new FormData(event.currentTarget);try{await api(`/api/v1/designer/services/${service.id}`,{method:"PATCH",body:JSON.stringify({name:f.get("name"),price:Number(f.get("price")),priceMax:f.get("priceMax")?Number(f.get("priceMax")):null,durationMinutes:Number(f.get("duration")),description:String(f.get("description")||""),priceNotes:String(f.get("priceNotes")||"").split("\n").filter(Boolean).slice(0,3)})},token);setMessage("시술 메뉴를 저장했어요.");await load();}catch(e){setMessage(errorMessage(e));}}}><Field label="시술 이름" name="name" minLength={2} maxLength={100} defaultValue={service.name} required/><Field label="가격" name="price" type="number" min={0} max={10000000} defaultValue={service.price} required/><Field label="최대 가격 (선택)" name="priceMax" type="number" min={0} defaultValue={service.price_max||""}/><Field label="소요 시간 (분)" name="duration" type="number" min={30} max={480} step={30} defaultValue={service.duration_minutes} required/><label className="field"><span>설명</span><textarea name="description" maxLength={500} defaultValue={service.description||""}/></label><label className="field"><span>추가금 조건 (줄을 비우면 삭제해요)</span><textarea name="priceNotes" maxLength={300} defaultValue={Array.isArray(service.price_notes)?service.price_notes.join("\n"):JSON.parse(service.price_notes||"[]").join("\n")}/></label><button className="button button-outline">메뉴 저장</button></form><button className="text-button" onClick={async()=>{if(!window.confirm("이 시술 메뉴를 삭제할까요? 진행 중 예약이 있으면 지울 수 없어요."))return;try{await api(`/api/v1/designer/services/${service.id}`,{method:"DELETE"},token);await load();}catch(e){setMessage(errorMessage(e));}}}>시술 메뉴 삭제</button></details>
                  <strong>
                    {formatWon(service.price)}
                    {service.price_max && service.price_max !== service.price
                      ? `–${formatWon(service.price_max)}`
                      : ""}
                  </strong>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <section className="panel" aria-labelledby="booking-feedback-heading">
        <div className="panel-title">
          <span>
            <h2 id="booking-feedback-heading">새 예약 알림음</h2>
            <p>
              이 브라우저에서만, 새 예약·확정·재방문 후보에 짧은 소리와 햅틱을
              사용합니다.
            </p>
          </span>
        </div>
        <label className="field">
          <span>알림음 켜기</span>
          <input
            type="checkbox"
            checked={feedbackEnabled}
            onChange={(event) => {
              const next = event.target.checked;
              setFeedbackEnabled(next);
              setBookingFeedbackEnabled(next);
            }}
          />
        </label>
        <p className="muted">
          감소된 모션 설정이나 백그라운드 탭에서는 재생하지 않습니다.
        </p>
      </section>

      <section className="panel"><h2>함께 일하는 동료 초대</h2><p>보상·수수료 없이 각자 이름으로 예약받아요.</p>{Boolean(me.invitedCount) && <p>초대해서 가입한 동료 {me.invitedCount}명</p>}<button className="button button-outline" onClick={() => { void navigator.clipboard.writeText(`${window.location.origin}/ko/start?invite=${me.profile.slug}`).then(()=> {setMessage("동료 초대 링크를 복사했어요.");track("invite_link_shared",{channel:"copy"});}); }}>동료 초대 링크 복사</button></section>
      <DesignerInbox token={token}/><ReviewReplies token={token}/>
      <BookingRulesPanel token={token} />
      <ShareTools slug={me.profile.slug} name={me.profile.salon_name||me.user.name} region={me.profile.region_label||""} service={services[0]?.name||"헤어 시술"}/><InstallHint/>
      <PromoPanel token={token} slug={me.profile.slug}/><PlanPanel token={token}/><RecoverySettings token={token}/><DataTools token={token}/><SupportPanel token={token}/>
      <PrivacyControls token={token} />

      </div>
      {portalTab === "today" && <><ContactQueue token={token} kind="reminder"/><OperationsPanel token={token} /></>}
      <section className="panel bookings-panel" id="booking-manage" hidden={portalTab !== "today"}>
        <div className="panel-title">
          <span>
            <h2>예약 관리</h2>
            <p>고객 요청을 확인하고 상태를 바꾸세요.</p>
          </span>
        </div>
        {bookings.length === 0 ? (
          <Empty
            icon={<CalendarDays />}
            title="아직 예약이 없어요"
            text="예약 링크를 기존 고객에게 공유해 첫 예약을 받아 보세요."
          />
        ) : (
          <div className="booking-list">
            {bookings.map((booking) => (
              <article key={booking.id}>
                <div>
                  <p className="booking-time">
                    {formatDateTime(booking.scheduled_at)}
                  </p>
                  <h3>
                    {booking.customer_name} · {booking.service_name}
                  </h3>
                  <p>
                    {booking.customer_phone}
                    {booking.customer_email
                      ? ` · ${booking.customer_email}`
                      : ""}
                  </p>
                  {booking.customer_notes && (
                    <small>요청: {booking.customer_notes}</small>
                  )}
                  <small>유입: {booking.acquisition_source || "direct"}</small>
                  {(() => {
                    try {
                      const answers = JSON.parse(booking.intake_answers || "[]") as Array<{
                        label: string;
                        answer: string;
                      }>;
                      return answers
                        .filter((answer) => answer.answer)
                        .map((answer) => (
                          <small key={answer.label}>상담 · {answer.label}: {answer.answer}</small>
                        ));
                    } catch {
                      return null;
                    }
                  })()}
                  {Number(booking.pending_change_requests || 0) > 0 && (
                    <small className="review-note">
                      고객이 시간을 바꾸고 싶어 해요. 등록된 연락처로 조율해 주세요.
                    </small>
                  )}
                  {booking.review_rating && (
                    <div className="review-note">
                      <small>
                        완료 예약 후기 {"★".repeat(booking.review_rating)} · {booking.review_status === "published" ? "공개 중" : booking.review_status === "under_review" ? "검토 중" : "비공개"}
                        {booking.review_comment ? ` · ${booking.review_comment}` : ""}
                      </small>
                      {booking.dispute_note && <small>제출한 이의: {booking.dispute_note}</small>}
                      {booking.review_id && booking.review_status === "published" && (
                        <details className="review-dispute">
                          <summary>후기 내용에 이의가 있어요</summary>
                          <form onSubmit={(event) => void disputeReview(event, booking.review_id!)}>
                            <textarea name="note" minLength={3} maxLength={1000} placeholder="예약 사실과 다른 점 또는 개인정보 문제를 구체적으로 적어 주세요" required />
                            <button className="button button-outline">비공개 검토 요청</button>
                          </form>
                        </details>
                      )}
                    </div>
                  )}
                </div>
                {booking.deposit_status==="requested"&&<button className="button button-outline" onClick={()=>void api(`/api/v1/business/calendar/bookings/${booking.id}/deposit`,{method:"PATCH"},token).then(()=>{setMessage("입금 확인을 기록했어요.");return load();}).catch(e=>setMessage(errorMessage(e)))}>입금 확인</button>}{booking.deposit_status==="confirmed"&&<p>입금 확인됨</p>}
                {booking.promo_code&&<p>할인 링크 · {formatWon(booking.promo_discount||0)}</p>}
                <DesignerBookingChanges token={token} bookingId={booking.id}/>
                {booking.style_asset_path&&<img className="booking-style-thumb" src={`${API_URL}${booking.style_asset_path}`} alt="손님이 고른 원하는 작업"/>}
                {(Boolean(booking.no_show_count)||Boolean(booking.late_count)) && <p>지난 노쇼 {booking.no_show_count}회 · 지각 {booking.late_count}회</p>}
                <label className="field"><span>지각 기록 (분)</span><input type="number" min={0} max={480} defaultValue={booking.late_minutes||0} onBlur={event=>{const minutes=Number(event.target.value);if(minutes!==(booking.late_minutes||0))void api(`/api/v1/business/ops/bookings/${booking.id}/late`,{method:"PATCH",body:JSON.stringify({minutes})},token).then(load).catch(e=>setMessage(errorMessage(e)));}}/></label>
                {booking.status === "confirmed" && booking.client_id && <BookingPassAction token={token} clientId={booking.client_id} bookingId={booking.id} onChanged={load} />}
                {booking.status === "confirmed" && <ContactQueue token={token} kind="confirm" bookingId={booking.id} />}
                {booking.status === "completed" && <button className="button button-outline" onClick={()=>void api<{managementUrl:string;scheduledAt:string}>(`/api/v1/business/ops/bookings/${booking.id}/next`,{method:"POST"},token).then(async result=>{setMessage(`${formatDateTime(result.scheduledAt)} 다음 예약을 잡았어요. 확인 주소: ${result.managementUrl}`);await load();}).catch(e=>setMessage(errorMessage(e)))}>다음 주기에 같은 시술 예약 잡기</button>}
                <div className="booking-actions">
                  <StatusPill status={booking.status} />
                  <label className="field"><span>취소 사유</span><select value={bookingReasons[booking.id]||"other"} onChange={event=>setBookingReasons(current=>({...current,[booking.id]:event.target.value}))}><option value="other">기타</option><option value="schedule_change">일정이 생겼어요</option><option value="unavailable">시술이 어려워요</option><option value="customer_request">손님 요청</option></select></label>
                  {booking.status === "pending" && (
                    <button
                      onClick={() => void changeStatus(booking.id, "confirmed")}
                    >
                      확정
                    </button>
                  )}
                  {booking.status !== "cancelled" && (
                      <button
                        onClick={() =>
                          void changeStatus(booking.id, "cancelled")
                        }
                      >
                        취소
                      </button>
                    )}
                  {booking.status === "confirmed" && (
                    <button
                      onClick={() => void changeStatus(booking.id, "completed")}
                    >
                      완료
                    </button>
                  )}
                  {/*
                    노쇼는 이 제품이 해결한다고 말하는 문제이고 API도
                    confirmed → no_show 전이를 받는데, 화면에 버튼이 없어
                    대시보드의 "노쇼 N건"이 영원히 0으로 고정돼 있었다.
                    노쇼로 기록해야 원장에 남고 재예약 후보에서도 빠진다.
                  */}
                  {booking.status === "confirmed" && (
                    <button
                      className="button-danger"
                      onClick={() => {
                        if (
                          !window.confirm(
                            "이 예약을 노쇼로 기록할까요? 고객 기록에 남고 되돌릴 수 없어요.",
                          )
                        )
                          return;
                        void changeStatus(booking.id, "no_show");
                      }}
                    >
                      노쇼
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
      <PortalShell tab={portalTab} onChange={changePortalTab} />
    </section>
  );
}

function PrivacyControls({ token }: { token: string }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  async function exportAccount() {
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch(`${API_URL}/api/v1/privacy/account-export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new ApiError("account_export_failed", response.status);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "hairbridge-account-export.json";
      link.click();
      URL.revokeObjectURL(url);
      setMessage("계정 데이터를 내려받을 수 있게 준비했어요.");
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }
  async function requestDeletion(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!window.confirm("14일 유예 뒤 계정 삭제 대상으로 전환할까요? 유예 기간에는 요청을 취소할 수 있습니다.")) return;
    setBusy(true);
    setMessage("");
    const form = new FormData(event.currentTarget);
    try {
      const result = await api<{ status: string; executeAfter: string }>(
        "/api/v1/privacy/account-deletion-request",
        { method: "POST", body: JSON.stringify({ password: form.get("password"), confirmation: "DELETE MY HAIRBRIDGE ACCOUNT" }) },
        token,
      );
      setMessage(`삭제 요청을 접수했습니다. 유예 종료: ${formatDateTime(result.executeAfter)}`);
      event.currentTarget.reset();
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel" aria-labelledby="privacy-controls-heading">
      <div className="panel-title">
        <span><h2 id="privacy-controls-heading">내 데이터와 계정</h2><p>계정 데이터를 내려받거나 본인 확인 뒤 삭제 유예를 시작할 수 있습니다.</p></span>
      </div>
      <button className="button button-outline" disabled={busy} onClick={() => void exportAccount()}>계정 데이터 내보내기</button>
      <form onSubmit={requestDeletion}>
        <Field label="삭제 요청 본인 확인 비밀번호" name="password" type="password" minLength={12} maxLength={128} required autoComplete="current-password" />
        <button className="button button-danger" disabled={busy}>14일 유예 삭제 요청</button>
      </form>
      {message && <Status kind="success">{message}</Status>}
    </section>
  );
}

interface DirectoryDesigner {
  user_id: string;
  name: string;
  slug: string;
  salon_name: string | null;
  bio: string | null;
  min_price: number | null;
  review_count: number;
  rating: number | null;
}

function DesignerDirectory() {
  const accessToken = localStorage.getItem("hb_access_token") || "";
  const [designers, setDesigners] = useState<DirectoryDesigner[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [showFavorites, setShowFavorites] = useState(false);
  const [favoriteSlugs, setFavoriteSlugs] = useState<string[]>(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem("hb_favorite_designers") || "[]",
      );
      return Array.isArray(saved)
        ? saved.filter((item): item is string => typeof item === "string")
        : [];
    } catch {
      return [];
    }
  });

  async function toggleFavorite(designer: DirectoryDesigner) {
    const slug = designer.slug;
    const wasFavorite = favoriteSlugs.includes(slug);
    setFavoriteSlugs((current) => {
      const next = current.includes(slug)
        ? current.filter((item) => item !== slug)
        : [...current, slug];
      localStorage.setItem("hb_favorite_designers", JSON.stringify(next));
      return next;
    });
    if (accessToken) {
      try {
        await api(
          `/api/v1/account/favorites/${encodeURIComponent(designer.user_id)}`,
          { method: wasFavorite ? "DELETE" : "PUT" },
          accessToken,
        );
      } catch (error) {
        setMessage(errorMessage(error));
        setFavoriteSlugs((current) =>
          wasFavorite
            ? Array.from(new Set([...current, slug]))
            : current.filter((item) => item !== slug),
        );
      }
    }
  }

  const load = useCallback(
    async (nextQuery = "") => {
      setLoading(true);
      setMessage("");
      try {
        const rows = await api<DirectoryDesigner[]>(
          `/api/v1/public/designers?q=${encodeURIComponent(nextQuery)}`,
        );
        setDesigners(rows);
        if (accessToken) {
          let saved: unknown = [];
          try {
            saved = JSON.parse(
              localStorage.getItem("hb_favorite_designers") || "[]",
            );
          } catch {
            localStorage.removeItem("hb_favorite_designers");
          }
          const localSlugs = Array.isArray(saved)
            ? saved.filter((item): item is string => typeof item === "string")
            : [];
          const synced = await api<DirectoryDesigner[]>(
            "/api/v1/account/favorites/sync",
            { method: "POST", body: JSON.stringify({ slugs: localSlugs }) },
            accessToken,
          );
          const slugs = synced.map((item) => item.slug);
          setFavoriteSlugs(slugs);
          localStorage.setItem("hb_favorite_designers", JSON.stringify(slugs));
        }
      } catch (error) {
        setMessage(errorMessage(error));
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const visibleDesigners = showFavorites
    ? designers.filter((designer) => favoriteSlugs.includes(designer.slug))
    : designers;

  return (
    <main className="directory-page">
      <section className="directory-hero">
        <p className="eyebrow">디자이너 찾기</p>
        <h1>나에게 맞는 디자이너를 찾아보세요.</h1>
        <p>공개된 실제 예약 페이지와 서비스, 후기를 기준으로 탐색합니다.</p>
        <form
          className="directory-search"
          onSubmit={(event) => {
            event.preventDefault();
            void load(query);
          }}
        >
          <Search />
          <input
            aria-label="디자이너 검색"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="디자이너명, 살롱명, 소개 검색"
          />
          <button className="button">검색</button>
        </form>
      </section>
      <section className="directory-results" aria-live="polite">
        <div className="directory-toolbar">
          <p>
            {showFavorites
              ? `찜한 디자이너 ${visibleDesigners.length}명`
              : `공개 디자이너 ${designers.length}명`}
          </p>
          <div className="segmented directory-filter">
            <button
              className={!showFavorites ? "active" : ""}
              onClick={() => setShowFavorites(false)}
            >
              전체
            </button>
            <button
              className={showFavorites ? "active" : ""}
              onClick={() => setShowFavorites(true)}
            >
              <Heart /> 찜 {favoriteSlugs.length}
            </button>
          </div>
        </div>
        {loading ? (
          <Loading label="디자이너를 찾는 중" />
        ) : message ? (
          <Status kind="error">{message}</Status>
        ) : visibleDesigners.length === 0 ? (
          <Empty
            icon={showFavorites ? <Heart /> : <Search />}
            title={
              showFavorites
                ? "아직 찜한 디자이너가 없어요"
                : "검색 결과가 없어요"
            }
            text={
              showFavorites
                ? "마음에 드는 디자이너의 하트를 눌러 저장해 보세요."
                : "다른 이름이나 키워드로 다시 찾아보세요."
            }
          />
        ) : (
          <div className="designer-directory-grid">
            {visibleDesigners.map((designer) => (
              <article key={designer.user_id}>
                <button
                  className={
                    favoriteSlugs.includes(designer.slug)
                      ? "favorite-button is-favorite"
                      : "favorite-button"
                  }
                  aria-label={
                    favoriteSlugs.includes(designer.slug)
                      ? `${designer.salon_name || designer.name} 찜 해제`
                      : `${designer.salon_name || designer.name} 찜하기`
                  }
                  onClick={() => void toggleFavorite(designer)}
                >
                  <Heart />
                </button>
                <div className="profile-orb">
                  {(designer.salon_name || designer.name).slice(0, 2)}
                </div>
                <span>
                  <h2>{designer.salon_name || designer.name}</h2>
                  <small>{designer.name} 디자이너</small>
                </span>
                <p>{designer.bio || "예약 가능한 서비스를 확인해 보세요."}</p>
                <dl>
                  <div>
                    <dt>평점</dt>
                    <dd>
                      {designer.review_count
                        ? `${Number(designer.rating).toFixed(1)} (${designer.review_count})`
                        : "신규"}
                    </dd>
                  </div>
                  <div>
                    <dt>서비스</dt>
                    <dd>
                      {designer.min_price === null
                        ? "문의"
                        : `${formatWon(designer.min_price)}부터`}
                    </dd>
                  </div>
                </dl>
                <button
                  className="button button-full"
                  onClick={() => go(`/book/${designer.slug}`)}
                >
                  프로필·예약 보기 <ArrowRight />
                </button>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

interface ChatMessage {
  id: string;
  sender_type: "designer" | "customer";
  content: string;
  read_by_designer: number;
  read_by_customer: number;
  created_at: string;
}

function BookingChat({
  bookingId,
  managementToken,
  accessToken,
  perspective,
}: {
  bookingId?: string;
  managementToken?: string;
  accessToken?: string;
  perspective: "designer" | "customer";
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const endpoint =
    perspective === "designer"
      ? `/api/v1/chat/designer/bookings/${encodeURIComponent(bookingId || "")}`
      : `/api/v1/chat/manage/${encodeURIComponent(managementToken || "")}`;
  const load = useCallback(
    () =>
      api<ChatMessage[]>(endpoint, {}, accessToken)
        .then(setMessages)
        .catch((error) => setMessage(errorMessage(error))),
    [accessToken, endpoint],
  );
  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 8_000);
    return () => window.clearInterval(timer);
  }, [load]);

  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setBusy(true);
    setMessage("");
    try {
      await api(
        endpoint,
        {
          method: "POST",
          body: JSON.stringify({ content: form.get("content") }),
        },
        accessToken,
      );
      formElement.reset();
      await load();
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="booking-chat" aria-label="예약 채팅">
      <div className="chat-heading">
        <span>
          <MessageCircle />
          <b>예약 채팅</b>
        </span>
        <small>8초마다 새 메시지를 확인합니다.</small>
      </div>
      <div className="chat-messages" aria-live="polite">
        {messages.length === 0 ? (
          <p>예약과 관련해 궁금한 내용을 남겨 보세요.</p>
        ) : (
          messages.map((item) => {
            const mine = item.sender_type === perspective;
            return (
              <div key={item.id} className={mine ? "mine" : "theirs"}>
                <small>
                  {mine
                    ? "나"
                    : perspective === "designer"
                      ? "고객"
                      : "디자이너"}
                </small>
                <p>{item.content}</p>
                <time>{formatDateTime(item.created_at)}</time>
              </div>
            );
          })
        )}
      </div>
      {message && <Status kind="error">{message}</Status>}
      <form onSubmit={send}>
        <textarea
          name="content"
          aria-label="메시지"
          rows={2}
          maxLength={2000}
          placeholder="메시지를 입력하세요"
          required
        />
        <button className="button" disabled={busy}>
          {busy ? "전송 중…" : "보내기"}
        </button>
      </form>
    </section>
  );
}

interface BookingCreated {
  durationMinutes:number;quotedPrice?:{min:number;max:number};
  id: string;
  status: string;
  scheduledAt: string;
  managementToken: string;
  privacyRetention: string;
}
interface WaitlistCreated {
  id: string;
  status: string;
  managementToken: string;
  desiredDate: string;
}

const acquisitionValues = new Set([
  "direct",
  "instagram",
  "kakao",
  "naver",
  "google",
  "referral",
  "waitlist",
  "other",
]);

function PublicBooking({ slug }: { slug: string }) {
  const bookingRequestKey = useRef(crypto.randomUUID());
  const [data, setData] = useState<PublicData | null>(null);
  const [serviceId, setServiceId] = useState("");
  const [stylePortfolioId,setStylePortfolioId]=useState("");
  const [promo,setPromo]=useState<Promo|null>(null);
  const [prefill,setPrefill]=useState<{customer_name:string;customer_phone:string}|null>(null);
  const [extraServices,setExtraServices] = useState<string[]>([]);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState<string[]>([]);
  const [slot, setSlot] = useState("");
  const [loading, setLoading] = useState(true);
  const [slotLoading, setSlotLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [created, setCreated] = useState<BookingCreated | null>(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileAttempt, setTurnstileAttempt] = useState(0);
  const [waitlistTurnstileToken, setWaitlistTurnstileToken] = useState("");
  const [waitlistAttempt, setWaitlistAttempt] = useState(0);
  const [waitlistCreated, setWaitlistCreated] = useState<WaitlistCreated | null>(null);
  const [favorite, setFavorite] = useState(() => {
    try {
      const items = JSON.parse(localStorage.getItem("hb_favorite_designers") || "[]");
      return Array.isArray(items) && items.includes(slug);
    } catch {
      return false;
    }
  });

  const acquisitionSource = useMemo(() => {
    const value = new URLSearchParams(window.location.search).get("src") || "direct";
    return value === "qr" ? "referral" : acquisitionValues.has(value) ? value : "other";
  }, []);

  useEffect(() => {
    api<PublicData>(`/api/v1/public/designers/${encodeURIComponent(slug)}`)
      .then((result) => {
        const normalized = normalizePublicBookingData(result);
        setData(normalized);
        const query = new URLSearchParams(window.location.search);
        const requestedService = query.get("serviceId") || query.get("service") || "";
        const promoCode=query.get("promo");if(promoCode)void api<Promo>(`/api/v1/public/promos/${encodeURIComponent(promoCode)}`).then(setPromo).catch(()=>setMessage("할인 링크가 종료됐어요. 원래 가격으로 예약할 수 있어요."));
        const from=query.get("from");if(from)void api<{customer_name:string;customer_phone:string}>(`/api/v1/public/bookings/manage/${encodeURIComponent(from)}/rebook`).then(setPrefill).catch(e=>setMessage(errorMessage(e)));
        setServiceId(
          normalized.services.some((service) => service.id === requestedService)
            ? requestedService
            : normalized.services[0]?.id || "",
        );
        const requestedDate = query.get("date") || "";
        if (/^\d{4}-\d{2}-\d{2}$/.test(requestedDate)) setDate(requestedDate);
        else {const id=normalized.services.find(service=>service.id===requestedService)?.id||normalized.services[0]?.id;if(id)void(async()=>{for(let day=0;day<14;day++){const next=new Date(Date.now()+day*86400000).toLocaleDateString("en-CA",{timeZone:"Asia/Seoul"});const available=await api<string[]>(`/api/v1/public/designers/${encodeURIComponent(slug)}/availability?serviceId=${encodeURIComponent(id)}&date=${next}`);if(available.length){setDate(next);return;}}setMessage("앞으로 2주 안에는 빈 시간이 없어요. 다른 날짜를 골라 주세요.");})().catch(e=>setMessage(errorMessage(e)));}
        track("designer_profile_view", {
          designerId: normalized.designer.user_id,
          slug,
        });
      })
      .catch((error) => setMessage(errorMessage(error)))
      .finally(() => setLoading(false));
  }, [slug]);
  useEffect(() => {
    if (!data) return;
    const name = data.designer.salon_name || `${data.designer.name} 디자이너`;
    const description = (data.designer.bio || `${name}의 서비스와 가능한 시간을 확인하고 가입 없이 예약하세요.`).slice(0, 160);
    const canonical = `${window.location.origin}/${locale}/designer/${encodeURIComponent(slug)}`;
    const previousTitle = document.title;
    document.title = `${name} 예약 | HairBridge`;
    const canonicalElement = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const previousCanonical = canonicalElement?.href;
    if (canonicalElement) canonicalElement.href = canonical;
    const previousMeta = new Map<string, string>();
    const updateMeta = (selector: string, content: string) => {
      const element = document.querySelector<HTMLMetaElement>(selector);
      if (element) {
        previousMeta.set(selector, element.content);
        element.content = content;
      }
    };
    updateMeta('meta[name="description"]', description);
    updateMeta('meta[property="og:title"]', `${name} 예약 | HairBridge`);
    updateMeta('meta[property="og:description"]', description);
    updateMeta('meta[property="og:url"]', canonical);
    const script = document.createElement("script");
    script.id = "hairbridge-designer-jsonld";
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "HairSalon",
      name,
      description,
      url: canonical,
      employee: { "@type": "Person", name: data.designer.name },
      makesOffer: data.services.map((service) => ({
        "@type": "Offer",
        priceCurrency: "KRW",
        price: service.price,
        itemOffered: { "@type": "Service", name: service.name },
      })),
    });
    document.head.appendChild(script);
    return () => {
      document.title = previousTitle;
      if (canonicalElement && previousCanonical) canonicalElement.href = previousCanonical;
      previousMeta.forEach((content, selector) => {
        const element = document.querySelector<HTMLMetaElement>(selector);
        if (element) element.content = content;
      });
      script.remove();
    };
  }, [data, slug]);
  useEffect(() => {
    if (!serviceId || !date) {
      setSlots([]);
      return;
    }
    setSlotLoading(true);
    setSlot("");
    api<string[]>(
      `/api/v1/public/designers/${encodeURIComponent(slug)}/availability?serviceId=${encodeURIComponent(serviceId)}&date=${date}&serviceIds=${encodeURIComponent([serviceId,...extraServices].join(","))}`,
    )
      .then(values=>{setSlots(values);const requested=new URLSearchParams(window.location.search).get("slot");if(requested&&values.includes(requested))setSlot(requested);})
      .catch((error) => setMessage(errorMessage(error)))
      .finally(() => setSlotLoading(false));
  }, [slug, serviceId, date, extraServices]);

  const dates = useMemo(
    () =>
      Array.from({ length: 14 }, (_, offset) => {
        const next = new Date(Date.now() + offset * 24 * 60 * 60 * 1000);
        const value = new Intl.DateTimeFormat("en-CA", {
          timeZone: "Asia/Seoul",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).format(next);
        return {
          value,
          label: new Intl.DateTimeFormat("ko-KR", {
            timeZone: "Asia/Seoul",
            month: "short",
            day: "numeric",
            weekday: "short",
          }).format(next),
        };
      }),
    [],
  );

  async function book(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!slot) {
      setMessage("예약 시간을 먼저 선택해 주세요.");
      return;
    }
    setMessage("");
    const form = new FormData(event.currentTarget);
    const selectedService = data?.services.find((service) => service.id === serviceId);
    const questions = selectedService?.intake_questions || [];
    const intakeQuestions = Array.isArray(questions)
      ? questions
      : (() => {
          try {
            return JSON.parse(questions || "[]") as Array<{ label: string; required: boolean }>;
          } catch {
            return [];
          }
        })();
    try {
      const result = await api<BookingCreated>(
        `/api/v1/public/designers/${encodeURIComponent(slug)}/bookings`,
        {
          method: "POST",
          headers: { "Idempotency-Key": bookingRequestKey.current },
          body: JSON.stringify({
            serviceId,
            serviceIds:[serviceId,...extraServices],
            stylePortfolioId:stylePortfolioId||undefined,
            promoCode:promo?.code,
            entryChannel:new URLSearchParams(window.location.search).get("src")==="qr"?"qr":undefined,
            scheduledAt: slot,
            customerName: form.get("customerName"),
            customerPhone: form.get("customerPhone"),
            customerEmail: form.get("customerEmail") || "",
            customerNotes: form.get("customerNotes") || "",
            intakeAnswers: intakeQuestions.map((question, index) => ({
              label: question.label,
              answer: String(form.get(`intake-${index}`) || ""),
            })),
            acquisitionSource,
            privacyConsent: form.get("privacyConsent") === "on",
            turnstileToken: turnstileToken || undefined,
          }),
        },
        localStorage.getItem("hb_access_token") || undefined,
      );
      setCreated(result);
      if (!localStorage.getItem("hb_access_token")) {
        localStorage.setItem("hb_pending_booking_token", result.managementToken);
      }
    } catch (error) {
      // 예약 화면에서는 "확정되지 않았다"를 반드시 말해야 한다. 고객이
      // 예약이 들어갔는지 모른 채 떠나면 노쇼와 중복 예약이 생긴다.
      // 공용 문구는 맥락이 없으므로 여기에서만 덧붙인다.
      setMessage(
        `${errorMessage(error)} 예약은 확정되지 않았으니 최신 가능 시간을 다시 확인해 주세요.`,
      );
      setTurnstileToken("");
      setTurnstileAttempt((value) => value + 1);
    }
  }

  async function joinWaitlist(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!date || !serviceId) return;
    const form = new FormData(event.currentTarget);
    setMessage("");
    try {
      setWaitlistCreated(
        await api<WaitlistCreated>(
          `/api/v1/public/designers/${encodeURIComponent(slug)}/waitlist`,
          {
            method: "POST",
            body: JSON.stringify({
              serviceId,
              desiredDate: date,
              timeWindow: form.get("timeWindow"),
              customerName: form.get("customerName"),
              customerPhone: form.get("customerPhone"),
              customerEmail: form.get("customerEmail") || "",
              note: form.get("note") || "",
              acquisitionSource,
              privacyConsent: form.get("privacyConsent") === "on",
              turnstileToken: waitlistTurnstileToken || undefined,
            }),
          },
        ),
      );
    } catch (error) {
      setMessage(errorMessage(error));
      setWaitlistTurnstileToken("");
      setWaitlistAttempt((value) => value + 1);
    }
  }

  async function togglePublicFavorite() {
    if (!data) return;
    let current: string[] = [];
    try {
      const saved = JSON.parse(
        localStorage.getItem("hb_favorite_designers") || "[]",
      );
      current = Array.isArray(saved)
        ? saved.filter((item): item is string => typeof item === "string")
        : [];
    } catch {
      current = [];
    }
    const next = favorite
      ? current.filter((item) => item !== slug)
      : Array.from(new Set([...current, slug]));
    setFavorite(!favorite);
    localStorage.setItem("hb_favorite_designers", JSON.stringify(next));
    const accessToken = localStorage.getItem("hb_access_token") || "";
    if (!accessToken) return;
    try {
      await api(
        `/api/v1/account/favorites/${encodeURIComponent(data.designer.user_id)}`,
        { method: favorite ? "DELETE" : "PUT" },
        accessToken,
      );
      track("favorite_changed", {
        designerId: data.designer.user_id,
        favorited: !favorite,
      });
    } catch (error) {
      setFavorite(favorite);
      localStorage.setItem("hb_favorite_designers", JSON.stringify(current));
      setMessage(errorMessage(error));
    }
  }

  if (loading)
    return (
      <main className="public-page">
        <Loading label="예약 페이지를 불러오는 중" />
      </main>
    );
  if (!data)
    return (
      <main className="public-page">
        <Status kind="error">{message}</Status>
      </main>
    );
  if (created) {
    const manageUrl = `${window.location.origin}/${locale}/manage/${created.managementToken}`;
    return (
      <main className="public-page">
        <section className="booking-success panel">
          <div className="success-icon">
            <Check />
          </div>
          <p>{created.durationMinutes}분{created.quotedPrice?` · 안내 금액 ${formatWon(created.quotedPrice.min)}${created.quotedPrice.max!==created.quotedPrice.min?`–${formatWon(created.quotedPrice.max)}`:""}`:""}</p>
          <p className="eyebrow">{created.status === "confirmed" ? "예약 확정" : "예약 요청 완료"}</p>
          <h1>{formatDateTime(created.scheduledAt)}</h1>
          <p>
            디자이너가 확인하면 예약 상태가 바뀝니다. 아래 링크를 저장하면
            예약을 확인하거나 취소할 수 있어요.
          </p>
          {/*
            이메일은 선택 항목이고 발송 provider도 아직 꺼져 있다. 관리 링크가
            클립보드에만 있으면 탭을 닫는 순간 고객은 자기 예약으로 돌아올 수
            없고, 취소 대신 그냥 안 나타난다 — 그게 노쇼다. 링크를 눈에 보이게
            두고 카카오톡·문자로 자신에게 보낼 수 있는 경로를 함께 준다.
          */}
          <p className="manage-url" aria-label="예약 확인 주소">
            <code>{manageUrl}</code>
          </p>
          <button
            className="button button-full"
            onClick={() => void navigator.clipboard.writeText(manageUrl)}
          >
            <Copy /> 예약 확인 링크 복사
          </button>
          {typeof navigator.share === "function" && (
            <button
              className="button button-outline button-full"
              onClick={() =>
                void navigator
                  .share({
                    title: "내 헤어 예약 확인 링크",
                    text: `${formatDateTime(created.scheduledAt)} 예약을 확인하거나 취소할 수 있는 링크예요.`,
                    url: manageUrl,
                  })
                  .catch(() => undefined)
              }
            >
              <Share2 /> 카카오톡·문자로 나에게 보내기
            </button>
          )}
          <button
            className="button button-outline button-full"
            onClick={() =>
              downloadBookingCalendar({
                title: `${data.designer.salon_name || data.designer.name} · ${data.services.find((service) => service.id === serviceId)?.name || "헤어 예약"}`,
                startsAt: created.scheduledAt,
                durationMinutes:created.durationMinutes,
                status: created.status,
              })
            }
          >
            <CalendarDays /> 일정 파일 저장
          </button>
          <button
            className="text-button"
            onClick={() => go(`/manage/${created.managementToken}`)}
          >
            예약 상태 바로 보기 <ArrowRight />
          </button>
          <button
            className="button button-outline button-full"
            onClick={() => go("/account")}
          >
            <Heart />
            {localStorage.getItem("hb_access_token")
              ? "My HairBridge에서 이어보기"
              : "선택 계정으로 예약 이어보기"}
          </button>
          {!localStorage.getItem("hb_access_token") && (
            <small>
              계정은 선택입니다. 만들면 방금 예약이 자동으로 연결되고, 만들지
              않아도 위 관리 링크로 계속 확인할 수 있어요.
            </small>
          )}
          {data.services.find(service=>service.id===serviceId)?.price_notes?.map(note=><p key={note}>{note}</p>)}
          {data.rules && data.rules.deposit_amount > 0 && <section><h2>예약금 {formatWon(data.rules.deposit_amount)}</h2><p>{data.rules.deposit_note}</p><p>헤어브릿지는 돈을 받거나 옮기지 않아요.</p></section>}
          <a className="button button-outline" href={`${API_URL}/api/v1/public/bookings/manage/${encodeURIComponent(created.managementToken)}/calendar.ics`.replace(/^https?:/,"webcal:")}>달력에 넣고 변경도 받기</a><p>시간 변경·취소는 달력의 다음 동기화 때 반영돼요.</p>
          <InstallHint/>
          <small>{created.privacyRetention}</small>
        </section>
      </main>
    );
  }

  const pairs = Array.from(new Set(data.portfolio.map(item=>item.pair_group_id).filter(Boolean))).map(group=>data.portfolio.filter(item=>item.pair_group_id===group)).filter(items=>items.length===2);
  const pairedIds = new Set(pairs.flat().map(item=>item.id));
  const singles = data.portfolio.filter(item=>!pairedIds.has(item.id));
  const heroPortfolio = singles[0] || null;
  const galleryPortfolio = heroPortfolio
    ? singles.slice(1)
    : singles;
  const quote=combineBookingQuote(data.services.filter(service=>[serviceId,...extraServices].includes(service.id)));
  const selectedService = data.services.find((service) => service.id === serviceId);
  const selectedQuestions = (() => {
    const questions = selectedService?.intake_questions || [];
    if (Array.isArray(questions)) return questions;
    try {
      return JSON.parse(questions || "[]") as Array<{
        label: string;
        required: boolean;
      }>;
    } catch {
      return [];
    }
  })();

  return (
    <main className="public-page">
      <section
        className={
          heroPortfolio ? "public-profile has-cover" : "public-profile"
        }
      >
        {heroPortfolio && (
          <div className="public-profile-cover">
            <img
              src={`${API_URL}${heroPortfolio.asset_path}`}
              alt={heroPortfolio.alt_text}
            />
            <span>{heroPortfolio.title}</span>
          </div>
        )}
        <div className="public-profile-copy">
          <div className="profile-orb">
            {data.designer.name.slice(0, 2).toUpperCase()}
          </div>
          <p className="eyebrow">개인 헤어 디자이너</p>
          <h1>{data.designer.name} 디자이너</h1>
          <p className="public-profile-place">
            {data.designer.region_label || "활동 지역 준비 중"}
            {data.designer.salon_name ? ` · ${data.designer.salon_name}` : ""}
          </p>
          <p>
            {data.designer.bio ||
              "당신에게 어울리는 스타일을 함께 찾아드립니다."}
          </p>
          <div className="public-specialties" aria-label="전문 시술">
            {data.designer.specialties.map((specialty) => (
              <span key={specialty}>{specialty}</span>
            ))}
            {data.designer.career_started_year && (
              <span>
                {Math.max(
                  1,
                  new Date().getFullYear() -
                    data.designer.career_started_year +
                    1,
                )}년차
              </span>
            )}
          </div>
          {(() => {
            const state = openStateOf(data.designer.weekly_schedule);
            if (!state) return null;
            return (
              <p className={`public-open-state${state.open ? " is-open" : ""}`}>
                <span aria-hidden="true" className="public-open-state__dot" />
                {state.label}
              </p>
            );
          })()}
          {data.reviewSummary.count > 0 && (
            <p className="public-rating">
              ★ {Number(data.reviewSummary.average).toFixed(1)} · 고객 후기{" "}
              {data.reviewSummary.count}개
            </p>
          )}
          <div className="public-profile-trust">
            <span>
              <CalendarDays /> 가입 없이 예약
            </span>
            <span>
              <ShieldCheck /> 완료 예약 후기만 공개
            </span>
          </div>
          <button
            className={favorite ? "button button-outline is-favorite" : "button button-outline"}
            onClick={() => void togglePublicFavorite()}
          >
            <Heart /> {favorite ? "찜한 디자이너" : "다음에도 찾기"}
          </button>
        </div>
      </section>
      <InquiryForm slug={slug}/>
      <button className="text-button" onClick={()=>{const url=`${window.location.origin}/ko/book/${slug}`;if(navigator.share)void navigator.share({title:`${data.designer.name} 디자이너`,url}).then(()=>track("designer_shared_by_customer",{channel:"share"})).catch(()=>{});else void navigator.clipboard.writeText(url).then(()=>{setMessage("디자이너 링크를 복사했어요.");track("designer_shared_by_customer",{channel:"copy"});});}}>친구에게 디자이너 보내기</button>
      {pairs.map(items=><section className="portfolio-pair" key={items[0]?.pair_group_id} aria-label="시술 전후 비교">{[...items].sort((a,b)=>a.pair_role === "before" ? -1 : b.pair_role === "before" ? 1 : 0).map(item=><figure key={item.id}><img src={`${API_URL}${item.asset_path}`} alt={item.alt_text}/><figcaption>{item.pair_role === "before" ? "시술 전" : "시술 후"} · {item.title}</figcaption></figure>)}</section>)}
      {galleryPortfolio.length > 0 && (
        <section className="public-portfolio" aria-label="디자이너 포트폴리오">
          <div className="panel-title">
            <span>
              <h2>포트폴리오</h2>
              <p>디자이너가 직접 공개한 스타일입니다.</p>
            </span>
          </div>
          <div>
            {galleryPortfolio.map((item) => (
              <figure key={item.id}>
                <img src={`${API_URL}${item.asset_path}`} alt={item.alt_text} />
                <figcaption>
                  <span>
                    <b>{item.title}</b>
                    {item.style_tags.length > 0 && (
                      <small>{item.style_tags.join(" · ")}</small>
                    )}
                  </span>
                  {item.service_id &&
                    (() => {
                      const service = data.services.find(
                        (candidate) => candidate.id === item.service_id,
                      );
                      return service ? (
                        <small>
                          {service.name} · {formatWon(service.price)}
                          {service.price_max && service.price_max !== service.price
                            ? `–${formatWon(service.price_max)}`
                            : ""}
                        </small>
                      ) : null;
                    })()}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}
      <div className="booking-trust-bar" aria-label="예약 방식 안내">
        <span>
          <b>01</b> 시술 선택
        </span>
        <span>
          <b>02</b> 날짜와 시간
        </span>
        <span>
          <b>03</b> 예약 요청
        </span>
      </div>
      {data.designer.instagram_handle && <a className="text-button" href={`https://instagram.com/${data.designer.instagram_handle}`} target="_blank" rel="noreferrer">인스타 @{data.designer.instagram_handle}</a>}
      {data.designer.credentials_note && <p>{data.designer.credentials_note}</p>}
      <form className="booking-form panel" onSubmit={book}>
        <section>
          <h2>
            <span>1</span> 시술 메뉴를 선택해 주세요
          </h2>
          <div className="service-choice">
            {data.services.map((service) => (
              <label
                key={service.id}
                className={serviceId === service.id ? "selected" : ""}
              >
                <input
                  type="radio"
                  name="service"
                  value={service.id}
                  checked={serviceId === service.id}
                  onChange={() => {setServiceId(service.id);setExtraServices([]);}}
                />
                <span>
                  <b>{service.name}</b>
                  <small>
                    {service.duration_minutes}분 ·{" "}
                    {service.description || "상담 후 진행"}
                  </small>
                </span>
                <strong>
                  {formatWon(service.price)}
                  {service.price_max && service.price_max !== service.price
                    ? `–${formatWon(service.price_max)}`
                    : ""}
                </strong>
              </label>
            ))}
            {/*
              2026-08-11 프로덕션 실측: 유일하게 공개된 예약 페이지(우정민
              디자이너)가 시술 0개인 채로 26일간 서빙됐다. 목록 자리가 그냥
              비어 있어서 고객에게는 고장난 페이지로 보이고, 아래 날짜와
              버튼만 남아 무엇을 해야 할지 알 수 없었다. 준비 중임을 말하고
              연락 경로를 주는 것이 최소한이다.
            */}
            {data.services.length === 0 && (
              <div className="service-empty">
                <CalendarDays />
                <span>
                  <b>아직 예약받는 시술이 없어요.</b>
                  {/*
                    공개 API는 디자이너 연락처를 내려주지 않는다(의도된 설계).
                    여기서 전화 CTA를 만들려면 연락처를 공개 표면에 노출해야
                    하므로 하지 않는다. 고객에게는 상태와 재방문 시점만 준다.
                  */}
                  <small>
                    {data.designer.salon_name || data.designer.name}님이 시술
                    메뉴를 준비하는 중이에요. 준비되면 이 페이지에서 바로
                    예약할 수 있어요.
                  </small>
                </span>
              </div>
            )}
          </div>
        </section>
        {quote&&<p>총 {quote.durationMinutes}분 · 안내 금액 {formatWon(quote.priceMin)}{quote.priceMax!==quote.priceMin?`–${formatWon(quote.priceMax)}`:""}</p>}
        {data.services.length > 1 && <section className="panel"><h2>함께 받을 시술 (선택)</h2>{data.services.filter(service=>service.id!==serviceId).map(service=><label className="consent" key={service.id}><input type="checkbox" checked={extraServices.includes(service.id)} onChange={event=>setExtraServices(current=>event.target.checked?[...current,service.id]:current.filter(id=>id!==service.id))}/><span>{service.name} · {service.duration_minutes}분 · {formatWon(service.price)}</span></label>)}</section>}

        {data.services.length > 0 && (
        <section>
          <h2>
            <span>2</span> 날짜와 시간을 골라 주세요
          </h2>
          <div className="date-choice">
            {dates.map((item) => (
              <button
                type="button"
                key={item.value}
                className={date === item.value ? "selected" : ""}
                onClick={() => setDate(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
          {date && (
            <div className="slot-choice" aria-live="polite">
              {slotLoading ? (
                <p>가능한 시간을 찾는 중…</p>
              ) : slots.length ? (
                /*
                  2026-08-11 네이버 예약 실화면(스테이뷰티) 대조: 한국 예약 UI는
                  시간을 오전/오후로 묶고 12시간제로 보여준다. 24시간제 단일
                  나열은 고객이 "14:00"을 한 번 환산해야 하고, 오전 자리가
                  있는지 한눈에 보이지 않는다. 같은 관습을 따른다.
                */
                slotGroups(slots).map((group) => (
                  <div className="slot-group" key={group.label}>
                    <p className="slot-group-label">{group.label}</p>
                    <div className="slot-group-grid">
                      {group.values.map((value) => (
                        <button
                          type="button"
                          key={value}
                          className={slot === value ? "selected" : ""}
                          onClick={() => setSlot(value)}
                        >
                          {formatSlotTime(value)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="slot-empty">
                  <CalendarDays />
                  <span>
                    <b>이 날짜는 예약이 모두 찼어요.</b>
                    <small>
                      다른 날짜를 선택하면 가능한 시간을 바로 보여드릴게요.
                    </small>
                  </span>
                </div>
              )}
            </div>
          )}
        </section>
        )}
        {data.services.length > 0 && (
        <section>
          <h2>
            <span>3</span> 예약자 정보를 알려 주세요
          </h2>
          <div className="form-grid">
            <Field label="이름" name="customerName" defaultValue={prefill?.customer_name||""} minLength={2} required />
            <Field
              label="연락처"
              name="customerPhone" defaultValue={prefill?.customer_phone||""}
              placeholder="010-0000-0000"
              required
            />
          </div>
          <Field
            label="이메일 (선택)"
            name="customerEmail"
            type="email"
            placeholder="예약 확인용"
          />
          {selectedQuestions.length > 0 && (
            <div className="intake-fields">
              <h3>시술 전 상담</h3>
              <p>디자이너가 준비할 수 있도록 필요한 내용만 알려주세요.</p>
              {selectedQuestions.map((question, index) => (
                <label className="field" key={`${question.label}-${index}`}>
                  <span>{question.label}</span>
                  <textarea
                    name={`intake-${index}`}
                    rows={2}
                    maxLength={500}
                    required={question.required}
                  />
                </label>
              ))}
            </div>
          )}
          {data.portfolio.length>0&&<fieldset><legend>이것처럼 받고 싶은 작업 (선택)</legend>{data.portfolio.map(item=><label className="style-choice" key={item.id}><input type="radio" name="desiredStyle" checked={stylePortfolioId===item.id} onChange={()=>setStylePortfolioId(item.id)}/><img src={`${API_URL}${item.asset_path}`} alt={item.alt_text}/><span>{item.title}</span></label>)}<button type="button" className="text-button" onClick={()=>setStylePortfolioId("")}>선택 풀기</button></fieldset>}
          <label className="field">
            <span>요청 사항 (선택)</span>
            <textarea name="customerNotes" maxLength={500} rows={3} />
          </label>
          {selectedService?.price_notes?.map(note=><p key={note}>{note}</p>)}
          <label className="consent">
            <input type="checkbox" name="privacyConsent" required />
            <span>
              <b>만 14세 이상이에요. 이름·연락처·요청 내용이 예약 확인을 위해 선택한 디자이너에게 전달되는 데 동의해요.</b>
              <small>
                만 14세 미만은 보호자가 대신 예약해 주세요. 예약일로부터 1년간 보관해요.{" "}
                <button type="button" onClick={() => go("/privacy")}>
                  자세히
                </button>
              </small>
            </span>
          </label>
          <details><summary>개인정보 수집·전달 자세히</summary><p>받는 사람: {data.designer.salon_name||data.designer.name} 디자이너</p><p>항목: 이름·연락처·선택한 이메일·예약 시간·요청 내용·상담 답변</p><p>목적: 예약 확인·변경·미용 서비스 준비</p><p>보관 기간: 예약일로부터 1년. 만 14세 미만은 보호자가 대신 예약해 주세요.</p><button type="button" className="text-button" onClick={()=>go("/privacy")}>처리방침 전체 보기</button></details>
          <Turnstile key={turnstileAttempt} onToken={setTurnstileToken} />
        </section>
        )}
        {message && <Status kind="error">{message}</Status>}
        {promo&&<p>{slot&&promoEligible(promo,slot)?`할인 적용 예정가 ${formatWon(Math.max(0,data.services.filter(service=>[serviceId,...extraServices].includes(service.id)).reduce((sum,service)=>sum+service.price,0)-promo.discount_krw))}`:"이 시간은 할인 조건이 아니에요. 원래 가격이 적용돼요."} · {promo.label}</p>}
        {data.rules && <p>{data.rules.policy_text || `예약 ${data.rules.cancel_cutoff_hours}시간 전까지 관리 링크에서 취소할 수 있어요.`}</p>}
        <p>시술 계약·가격·예약금·환불은 디자이너와 손님 사이에서 정해요. 헤어브릿지는 예약을 이어 줘요.</p>
        {data.services.length > 0 && (
          <button className="button button-full button-large" disabled={!slot}>
            예약 요청하기 <ArrowRight />
          </button>
        )}
      </form>
      {date && !slotLoading && slots.length === 0 && (
        <section className="panel waitlist-public">
          {waitlistCreated ? (
            <div className="waitlist-success">
              <BellRing />
              <span>
                <h2>대기목록에 등록됐어요.</h2>
                <p>
                  이 날짜에 같은 서비스 자리가 나면 안내를 준비합니다. 실제 예약은
                  예약 페이지에서 먼저 완료한 고객에게 확정됩니다.
                </p>
                <button
                  className="button button-outline"
                  onClick={() =>
                    go(`/waitlist/${waitlistCreated.managementToken}`)
                  }
                >
                  대기 요청 확인·철회
                </button>
              </span>
            </div>
          ) : (
            <form onSubmit={joinWaitlist}>
              <div className="panel-title">
                <span>
                  <p className="eyebrow">취소 자리 알림</p>
                  <h2>이 날짜에 자리가 나면 알려드릴까요?</h2>
                  <p>자동 예약이나 결제 없이 취소 자리 안내만 준비합니다.</p>
                </span>
              </div>
              <label className="field">
                <span>선호 시간대</span>
                <select name="timeWindow" defaultValue="any">
                  <option value="any">시간 무관</option>
                  <option value="morning">오전</option>
                  <option value="afternoon">오후</option>
                  <option value="evening">저녁</option>
                </select>
              </label>
              <div className="form-grid">
                <Field label="이름" name="customerName" minLength={2} required />
                <Field label="연락처" name="customerPhone" placeholder="010-0000-0000" required />
              </div>
              <Field label="이메일 (권장)" name="customerEmail" type="email" />
              <Field label="요청 메모 (선택)" name="note" maxLength={300} />
              <label className="consent compact">
                <input type="checkbox" name="privacyConsent" required />
                <span>취소 자리 안내를 위해 이름과 연락처를 저장하는 데 동의합니다.</span>
              </label>
              <Turnstile key={waitlistAttempt} onToken={setWaitlistTurnstileToken} />
              <button className="button button-full">대기목록 등록</button>
            </form>
          )}
        </section>
      )}
      {data.reviews.length > 0 && (
        <section className="panel public-reviews">
          <h2>고객 후기</h2>
          <div>
            {data.reviews.map((review) => (
              <article key={`${review.created_at}-${review.comment}`}>
                <b>{"★".repeat(review.rating)}</b>
                <p>{review.comment}</p>{review.edited_at&&<small>수정됨</small>}{review.designer_reply&&<p><b>디자이너 답글</b> {review.designer_reply}</p>}
              </article>
            ))}
          </div>
        </section>
      )}
      <div className="powered-by">
        Powered by <button onClick={() => go("/")}>HairBridge</button> · 고객은
        가입할 필요가 없습니다.
      </div>
    </main>
  );
}

interface ManagedWaitlist {
  id: string;
  desired_date: string;
  time_window: string;
  status: string;
  service_name: string;
  designer_name: string;
  slug: string;
}

function ManageWaitlist({ token }: { token: string }) {
  const [entry, setEntry] = useState<ManagedWaitlist | null>(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(
    () =>
      api<ManagedWaitlist>(
        `/api/v1/public/waitlist/manage/${encodeURIComponent(token)}`,
      )
        .then(setEntry)
        .catch((error) => setMessage(errorMessage(error)))
        .finally(() => setLoading(false)),
    [token],
  );
  useEffect(() => void load(), [load]);
  async function withdraw() {
    if (!window.confirm("이 대기 요청을 철회할까요?")) return;
    try {
      await api(
        `/api/v1/public/waitlist/manage/${encodeURIComponent(token)}`,
        { method: "DELETE" },
      );
      setMessage("대기 요청을 취소했어요.");
      await load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  if (loading)
    return (
      <main className="public-page"><Loading label="대기 요청을 확인하는 중" /></main>
    );
  return (
    <main className="public-page">
      <section className="panel manage-card">
        <p className="eyebrow">취소 자리 알림</p>
        <h1>{entry ? `${entry.designer_name} · ${entry.service_name}` : "대기 요청"}</h1>
        {entry && (
          <>
            <p>{entry.desired_date} · {entry.time_window}</p>
            <StatusPill status={entry.status} />
            <button className="button button-outline button-full" onClick={() => go(`/book/${entry.slug}?src=waitlist`)}>
              예약 가능 시간 다시 보기
            </button>
            {["waiting", "notified"].includes(entry.status) && (
              <button className="button button-danger button-full" onClick={() => void withdraw()}>
                대기 요청 철회
              </button>
            )}
          </>
        )}
        {message && <Status kind={entry ? "success" : "error"}>{message}</Status>}
      </section>
    </main>
  );
}

interface ManagedBooking {
  service_id:string; serviceIds?:string[];salon_address?:string|null;review_deleted_at?:string|null;review_edited_at?:string|null;
  changes?:Array<{actor:string;action:string;reason_code:string;from_at:string|null;to_at:string|null;created_at:string}>;
  passes?:Array<{kind:string;remaining:number}>;
  policy?:{text:string;cancelCutoffHours:number;changeCutoffHours:number};
  id: string;
  customer_name: string | null;
  scheduled_at: string;
  duration_minutes: number;
  status: string;
  service_name: string;
  slug: string;
  salon_name: string | null;
  designer_name: string;
  review_rating: number | null;
  review_comment: string | null;
  review_created_at: string | null;
  stylePhoto?:{id:string;title:string;asset_path:string}|null;
  privacy: BookingPrivacy;
  consultation: {
    desired_result: string | null;
    recent_chemical_history: string | null;
    scalp_sensitivity: "unknown" | "none" | "sometimes" | "frequent";
    reaction_history: string | null;
    safety_acknowledged_at: string;
    updated_at: string;
  } | null;
  rebookingCandidate: {
    id: string;
    due_at: string;
    interval_weeks: number;
    status: "pending" | "snoozed" | "dismissed" | "muted" | "booked";
  } | null;
}

function ManageBooking({ token }: { token: string }) {
  const [booking, setBooking] = useState<ManagedBooking | null>(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [consultationBusy, setConsultationBusy] = useState(false);
  const [privacyBusy, setPrivacyBusy] = useState<BookingPrivacyBusy>(null);
  const [privacyConfirmationOpen, setPrivacyConfirmationOpen] = useState(false);
  const [privacyNotice, setPrivacyNotice] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const load = useCallback(
    () =>
      api<ManagedBooking>(
        `/api/v1/public/bookings/manage/${encodeURIComponent(token)}`,
      )
        .then(setBooking)
        .finally(() => setLoading(false)),
    [token],
  );
  useEffect(() => {
    void load().catch((error) => setMessage(errorMessage(error)));
  }, [load]);
  async function cancel() {
    try {
      await api(`/api/v1/public/bookings/manage/${encodeURIComponent(token)}`, {
        method: "DELETE",
      });
      setMessage("예약을 취소했어요.");
      await load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  async function exportBookingData() {
    setPrivacyBusy("export");
    setPrivacyNotice(null);
    try {
      const response = await fetch(
        `${API_URL}/api/v1/public/bookings/manage/${encodeURIComponent(token)}/export`,
      );
      if (!response.ok) throw new ApiError("booking_export_failed", response.status);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "hairbridge-booking-export.json";
      link.click();
      URL.revokeObjectURL(url);
      setPrivacyNotice({
        kind: "success",
        text: "내 예약 정보를 내려받았어요.",
      });
    } catch (error) {
      setPrivacyNotice({ kind: "error", text: errorMessage(error) });
    } finally {
      setPrivacyBusy(null);
    }
  }
  async function deleteBookingPii() {
    setPrivacyBusy("delete");
    setPrivacyNotice(null);
    try {
      const receipt = await api<{
        status: "deleted" | "already_deleted";
        deletedAt: string;
      }>(`/api/v1/public/bookings/manage/${encodeURIComponent(token)}/pii`, {
        method: "DELETE",
      });
      setBooking((current) =>
        current
          ? {
              ...current,
              customer_name: null,
              consultation: null,
              privacy: {
                state: "deleted",
                canDelete: false,
                deletedAt: receipt.deletedAt,
                deletionScope: "booking_and_consultation",
              },
            }
          : current,
      );
      setPrivacyConfirmationOpen(false);
      setPrivacyNotice({
        kind: "success",
        text:
          receipt.status === "already_deleted"
            ? "이 예약의 연락처, 요청 메모와 방문 전 확인표는 이미 삭제돼 있어요."
            : "이 예약의 연락처, 요청 메모와 방문 전 확인표를 삭제했어요.",
      });
      await load();
    } catch (error) {
      setPrivacyNotice({ kind: "error", text: errorMessage(error) });
    } finally {
      setPrivacyBusy(null);
    }
  }
  async function submitReview(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setReviewBusy(true);
    setMessage("");
    const form = new FormData(event.currentTarget);
    try {
      await api(
        `/api/v1/public/bookings/manage/${encodeURIComponent(token)}/review`,
        {
          method: "POST",
          body: JSON.stringify({
            rating: Number(form.get("rating")),
            comment: form.get("comment") || "",
          }),
        },
      );
      setMessage("후기를 남겨 주셔서 고마워요.");
      await load();
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setReviewBusy(false);
    }
  }
  async function recordLifecycle(action: "confirmed" | "reschedule_requested") {
    setLifecycleBusy(true);
    setMessage("");
    try {
      await api(
        `/api/v1/public/bookings/manage/${encodeURIComponent(token)}/lifecycle`,
        { method: "POST", body: JSON.stringify({ action }) },
      );
      setMessage(
        action === "confirmed"
          ? "예약 시간을 확인했다고 디자이너에게 전했어요."
          : "시간 조정 요청을 전했어요. 예약 시간은 자동으로 바뀌지 않습니다.",
      );
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setLifecycleBusy(false);
    }
  }
  async function saveConsultation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setConsultationBusy(true);
    setMessage("");
    try {
      await api(
        `/api/v1/public/bookings/manage/${encodeURIComponent(token)}/consultation`,
        {
          method: "PUT",
          body: JSON.stringify({
            desiredResult: form.get("desiredResult") || "",
            recentChemicalHistory: form.get("recentChemicalHistory") || "",
            scalpSensitivity: form.get("scalpSensitivity"),
            reactionHistory: form.get("reactionHistory") || "",
            safetyAcknowledged: form.get("safetyAcknowledged") === "on",
          }),
        },
      );
      setMessage("시술 전 확인표를 디자이너에게 전달했어요.");
      await load();
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setConsultationBusy(false);
    }
  }
  async function updateRebooking(action: "dismiss" | "snooze" | "unsubscribe") {
    setLifecycleBusy(true);
    setMessage("");
    try {
      await api(
        `/api/v1/public/bookings/manage/${encodeURIComponent(token)}/rebooking-candidate`,
        {
          method: "POST",
          body: JSON.stringify({
            action,
            ...(action === "snooze" ? { snoozeWeeks: 2 } : {}),
          }),
        },
      );
      setMessage(
        action === "snooze"
          ? "2주 뒤에 다시 볼 수 있도록 미뤘어요."
          : "재방문 제안을 숨겼어요.",
      );
      await load();
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setLifecycleBusy(false);
    }
  }
  async function carryDesiredResult(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setConsultationBusy(true);
    setMessage("");
    try {
      await api(`/api/v1/public/bookings/manage/${encodeURIComponent(token)}/consultation/carry-forward`, {
        method: "POST",
        body: JSON.stringify({ sourceManagementToken: form.get("sourceManagementToken"), copyDesiredResult: true }),
      });
      setMessage("지난 예약에서 선택한 원하는 결과를 이번 확인표에 가져왔어요. 내용을 확인한 뒤 저장해 주세요.");
      await load();
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setConsultationBusy(false);
    }
  }
  if (loading)
    return (
      <main className="public-page">
        <Loading label="예약을 확인하는 중" />
      </main>
    );
  if (!booking)
    return (
      <main className="public-page">
        <Status kind="error">{message}</Status>
      </main>
    );
  const privacyControls = bookingPrivacyControls(
    booking.privacy,
    privacyBusy,
    privacyConfirmationOpen,
  );
  return (
    <main className="public-page">
      <section className="panel manage-card">
        <p className="eyebrow">예약 확인</p>
        <StatusPill status={booking.status} />
        <h1>{formatDateTime(booking.scheduled_at)}</h1>
        <dl>
          <div>
            <dt>디자이너</dt>
            <dd>{booking.salon_name || booking.designer_name}</dd>
          </div>
          <div>
            <dt>서비스</dt>
            <dd>{booking.service_name}</dd>
          </div>
          <div>
            <dt>예약한 사람</dt>
            <dd>{booking.customer_name || "예약 정보에서 삭제됨"}</dd>
          </div>
        </dl>
        <button
          className="button button-outline button-full"
          onClick={() =>
            downloadBookingCalendar({
              title: `${booking.salon_name || booking.designer_name} · ${booking.service_name}`,
              startsAt: booking.scheduled_at,
              durationMinutes: booking.duration_minutes,
              status: booking.status,
            })
          }
        >
          <CalendarDays /> 일정 파일 저장
        </button>
        {message && <Status kind="success">{message}</Status>}
        {booking.status === "confirmed" && (
          <section className="panel" aria-label="예약 시간 확인">
            <h2>이 시간이 괜찮으신가요?</h2>
            <p>
              부담 없이 확인하거나 조정을 요청할 수 있어요. 시간은 자동으로
              바뀌지 않습니다.
            </p>
            <div className="button-row">
              <button
                className="button"
                disabled={lifecycleBusy}
                onClick={() => void recordLifecycle("confirmed")}
              >
                이 시간 괜찮아요
              </button>
              <button
                className="button button-outline"
                disabled={lifecycleBusy}
                onClick={() => void recordLifecycle("reschedule_requested")}
              >
                시간 조정 요청
              </button>
            </div>
          </section>
        )}
        {["pending", "confirmed"].includes(booking.status) && (
          <form className="panel consultation-form" onSubmit={carryDesiredResult}>
            <p className="eyebrow">지난 예약에서 이어오기</p>
            <h2>원하는 결과만 선택해 가져오기</h2>
            <p>같은 디자이너에게 완료된 예약의 관리 링크를 가진 경우에만, 원하는 결과 한 항목을 이번 확인표로 가져옵니다. 두피·반응 기록과 사진은 복사하지 않아요.</p>
            <label className="field"><span>지난 예약 관리 링크의 마지막 코드</span><input name="sourceManagementToken" minLength={16} required autoComplete="off" /></label>
            <button className="button button-outline button-full" disabled={consultationBusy}>원하는 결과 가져오기</button>
          </form>
        )}
        {["pending", "confirmed"].includes(booking.status) && (
          <form className="panel consultation-form" onSubmit={saveConsultation}>
            <p className="eyebrow">방문 전에 1분</p>
            <h2>말로 반복하지 않는 시술 확인표</h2>
            <p>
              원하는 결과와 최근 시술 사실을 미리 알려 주세요. 의료 진단을 묻지 않으며,
              시술 가능 여부는 방문 후 디자이너가 다시 확인합니다.
            </p>
            <label className="field">
              <span>이번에 원하는 결과</span>
              <textarea
                name="desiredResult"
                rows={3}
                maxLength={1000}
                defaultValue={booking.consultation?.desired_result || ""}
                placeholder="길이, 색감, 손질할 때 가장 중요한 점"
              />
            </label>
            <label className="field">
              <span>최근 1년의 염색·펌·탈색 이력</span>
              <textarea
                name="recentChemicalHistory"
                rows={3}
                maxLength={1200}
                defaultValue={booking.consultation?.recent_chemical_history || ""}
                placeholder="대략적인 시기와 종류만 적어도 괜찮아요"
              />
            </label>
            <label className="field">
              <span>시술 중 두피가 따갑거나 붉어진 적</span>
              <select
                name="scalpSensitivity"
                defaultValue={booking.consultation?.scalp_sensitivity || "unknown"}
              >
                <option value="unknown">잘 모르겠어요</option>
                <option value="none">없었어요</option>
                <option value="sometimes">가끔 있었어요</option>
                <option value="frequent">자주 있었어요</option>
              </select>
            </label>
            <label className="field">
              <span>기억나는 반응과 회복 경과</span>
              <textarea
                name="reactionHistory"
                rows={3}
                maxLength={1200}
                defaultValue={booking.consultation?.reaction_history || ""}
                placeholder="없다면 비워 두세요"
              />
            </label>
            <label className="consent compact">
              <input type="checkbox" name="safetyAcknowledged" required />
              <span>
                이 내용은 예약·시술 안전 확인을 위해 디자이너에게 전달되고,
                내 예약 정보 내려받기와 삭제 범위에 포함됨을 확인했습니다.
              </span>
            </label>
            <button className="button button-full" disabled={consultationBusy}>
              {consultationBusy
                ? "전달 중…"
                : booking.consultation
                  ? "확인표 수정하기"
                  : "확인표 전달하기"}
            </button>
            {booking.consultation && (
              <small className="form-note">마지막 저장 {formatDateTime(booking.consultation.updated_at)}</small>
            )}
          </form>
        )}
        <a className="button button-outline" href={`${API_URL}/api/v1/public/bookings/manage/${encodeURIComponent(token)}/calendar.ics`.replace(/^https?:/,"webcal:")}>달력에 넣고 변경도 받기</a>
        {booking.salon_address&&<section className="panel"><h2>찾아가는 길</h2><p>{booking.salon_address}</p><button className="button button-outline" onClick={()=>void navigator.clipboard.writeText(booking.salon_address||"").then(()=>setMessage("주소를 복사했어요."))}>주소 복사</button><a className="button button-outline" href={`https://map.naver.com/p/search/${encodeURIComponent(booking.salon_address)}`} target="_blank" rel="noreferrer">지도 앱에서 길 보기</a></section>}
        {["pending","confirmed"].includes(booking.status)&&<CustomerReschedule token={token} slug={booking.slug} serviceId={booking.service_id} serviceIds={booking.serviceIds} scheduledAt={booking.scheduled_at} cutoff={booking.policy?.changeCutoffHours||0} onChanged={load}/>}
        {booking.status==="completed"&&<button className="button" onClick={()=>go(`/book/${booking.slug}?serviceId=${booking.service_id}&from=${encodeURIComponent(token)}`)}>같은 시술로 다시 예약</button>}
        <BookingReport managementToken={token}/><SupportPanel managementToken={token}/>
        <details><summary>예약 변경 기록</summary>{booking.changes?.length?booking.changes.map((change,index)=><p key={`${change.created_at}-${index}`}>{change.actor==="customer"?"손님이":change.actor==="designer"?"디자이너가":"시스템에서"} {formatDateTime(change.created_at)} {change.action==="cancelled"?"취소했어요":"시간을 바꿨어요"} · {change.reason_code==="schedule_change"?"일정이 생겼어요":change.reason_code==="customer_request"?"손님 요청":change.reason_code==="unavailable"?"시술이 어려워요":"기타 사유"}{change.to_at&&` · ${change.from_at?formatDateTime(change.from_at):""} → ${formatDateTime(change.to_at)}`}</p>):<p>이 기능 이전 예약은 변경 기록이 없어요.</p>}</details>
        {booking.review_rating&&!booking.review_deleted_at&&<CustomerReviewEditor path={`/api/v1/public/bookings/manage/${encodeURIComponent(token)}/review`} rating={booking.review_rating} comment={booking.review_comment||""} onChanged={load}/>}
        {booking.review_deleted_at&&<p>후기를 지웠어요.</p>}
        {booking.passes?.map((pass,index)=><p key={`${pass.kind}-${index}`}>회원권 잔액 · {pass.remaining.toLocaleString()}{pass.kind === "count" ? "회" : "원"}</p>)}
        {booking.policy && <p>{booking.policy.text || `예약 ${booking.policy.cancelCutoffHours}시간 전까지 취소할 수 있어요.`}</p>}
        {!["cancelled", "completed"].includes(booking.status) && (new Date(booking.scheduled_at).getTime()-Date.now() < (booking.policy?.cancelCutoffHours || 0)*3600000 ? <p>온라인 취소 마감이 지났어요. 위 예약 채팅으로 디자이너에게 연락해 주세요.</p> :
          <button
            className="button button-danger button-full"
            onClick={() => void cancel()}
          >
            예약 취소
          </button>
        )}
        {booking.status === "completed" && booking.review_rating && (
          <div className="submitted-review">
            <b>{"★".repeat(booking.review_rating)}</b>
            <p>{booking.review_comment || "평점을 남겨 주셔서 고마워요."}</p>
          </div>
        )}
        {booking.status === "completed" && !booking.review_rating && !booking.review_deleted_at && (
          <form className="review-form" onSubmit={submitReview}>
            <h2>서비스는 어떠셨나요?</h2>
            <label className="field">
              <span>평점</span>
              <select name="rating" defaultValue="5" required>
                <option value="5">★★★★★ 아주 만족해요</option>
                <option value="4">★★★★☆ 만족해요</option>
                <option value="3">★★★☆☆ 보통이에요</option>
                <option value="2">★★☆☆☆ 아쉬워요</option>
                <option value="1">★☆☆☆☆ 많이 아쉬워요</option>
              </select>
            </label>
            <label className="field">
              <span>후기 (선택)</span>
              <textarea name="comment" maxLength={1000} rows={3} />
            </label>
            <button className="button button-full" disabled={reviewBusy}>
              {reviewBusy ? "등록 중…" : "후기 등록"}
            </button>
          </form>
        )}
        {booking.status === "completed" &&
          booking.rebookingCandidate &&
          ["pending", "snoozed"].includes(
            booking.rebookingCandidate.status,
          ) && (
            <section className="panel" aria-label="다음 방문 후보">
              <p className="eyebrow">다음 방문 후보</p>
              <h2>
                {booking.rebookingCandidate.interval_weeks}주 뒤, 필요할 때만
                다시 살펴보세요.
              </h2>
              <p>
                예약을 강요하지 않으며, 원하지 않으면 숨기거나 나중에 다시 볼 수
                있어요.
              </p>
              <div className="button-row">
                <button
                  className="button"
                  onClick={() => go(`/book/${booking.slug}`)}
                >
                  새 예약 살펴보기
                </button>
                <button
                  className="button button-outline"
                  disabled={lifecycleBusy}
                  onClick={() => void updateRebooking("snooze")}
                >
                  2주 뒤 보기
                </button>
                <button
                  className="text-button"
                  disabled={lifecycleBusy}
                  onClick={() => void updateRebooking("dismiss")}
                >
                  이번에는 건너뛰기
                </button>
                <button
                  className="text-button"
                  disabled={lifecycleBusy}
                  onClick={() => void updateRebooking("unsubscribe")}
                >
                  이 제안 끄기
                </button>
              </div>
            </section>
          )}
        {booking.stylePhoto&&<figure className="panel"><img src={API_URL+booking.stylePhoto.asset_path} alt={booking.stylePhoto.title||"선택한 디자이너 작업"} width={180}/><figcaption>이 작업을 참고해 달라고 보냈어요.</figcaption></figure>}
        <section className="panel booking-privacy-panel" aria-label="내 정보 관리">
          <h2>내 정보 관리</h2>
          <p>{privacyControls.description}</p>
          <div className="button-row booking-privacy-actions">
            <button
              className="button button-outline"
              disabled={privacyControls.controlsDisabled}
              onClick={() => void exportBookingData()}
            >
              {privacyControls.exportLabel}
            </button>
            {privacyControls.showDeleteEntry && (
              <button
                className="text-button booking-privacy-delete-entry"
                aria-expanded={privacyConfirmationOpen}
                aria-controls="booking-privacy-confirmation"
                disabled={privacyControls.controlsDisabled}
                onClick={() => {
                  setPrivacyNotice(null);
                  setPrivacyConfirmationOpen(true);
                }}
              >
                연락처와 요청 내용 지우기
              </button>
            )}
          </div>
          {privacyControls.activeHelp && (
            <p className="booking-privacy-help">{privacyControls.activeHelp}</p>
          )}
          {privacyControls.showConfirmation && (
            <section
              className="booking-privacy-confirmation"
              id="booking-privacy-confirmation"
              aria-labelledby="booking-privacy-confirmation-title"
            >
              <h3 id="booking-privacy-confirmation-title">
                이 예약에서 내 정보를 지울까요?
              </h3>
              <p>
                이름, 연락처, 요청 메모와 방문 전 확인표를 지워요. 예약 날짜,
                시술명과 처리 상태 같은 예약 운영 기록은 남아요. 삭제하면
                되돌릴 수 없어요.
              </p>
              <div className="button-row">
                <button
                  className="button button-outline"
                  disabled={privacyControls.controlsDisabled}
                  onClick={() => setPrivacyConfirmationOpen(false)}
                >
                  돌아가기
                </button>
                <button
                  className="button button-danger"
                  disabled={privacyControls.controlsDisabled}
                  onClick={() => void deleteBookingPii()}
                >
                  {privacyControls.deleteLabel}
                </button>
              </div>
            </section>
          )}
          {privacyControls.showDeletedReceipt && !privacyNotice && (
            <Status kind="success">
              이 예약의 연락처, 요청 메모와 방문 전 확인표는 삭제돼 있어요.
            </Status>
          )}
          {privacyNotice && (
            <Status kind={privacyNotice.kind}>{privacyNotice.text}</Status>
          )}
        </section>
        <button
          className="text-button"
          onClick={() => go(`/book/${booking.slug}`)}
        >
          예약 페이지로 돌아가기
        </button>
      </section>
    </main>
  );
}

interface BookingActionResult {
  status: string;
  scheduled_at: string;
  duration_minutes: number;
  service_name: string;
  designer_name: string;
}

function BookingAction({ token }: { token: string }) {
  const [booking, setBooking] = useState<BookingActionResult | null>(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void api<BookingActionResult>(
      `/api/v1/public/booking-actions/${encodeURIComponent(token)}`,
    )
      .then(setBooking)
      .catch((error) => setMessage(errorMessage(error)))
      .finally(() => setLoading(false));
  }, [token]);

  async function answer(action: "confirmed" | "reschedule_requested") {
    setBusy(true);
    setMessage("");
    try {
      await api(`/api/v1/public/booking-actions/${encodeURIComponent(token)}`, {
        method: "POST",
        body: JSON.stringify({ action }),
      });
      setMessage(
        action === "confirmed"
          ? "예약 시간을 확인했다고 디자이너에게 전했어요."
          : "시간 조정 요청을 디자이너에게 전했어요. 예약 시간은 자동으로 바뀌지 않아요.",
      );
      setBooking(null);
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="public-page">
      <section className="panel manage-card">
        <p className="eyebrow">HairBridge 예약 알림</p>
        {loading ? (
          <Loading label="예약을 확인하는 중" />
        ) : booking ? (
          <>
            <h1>{formatDateTime(booking.scheduled_at)}</h1>
            <p>
              {booking.designer_name} · {booking.service_name}
            </p>
            <button
              className="button button-outline button-full"
              onClick={() =>
                downloadBookingCalendar({
                  title: `${booking.designer_name} · ${booking.service_name}`,
                  startsAt: booking.scheduled_at,
                  durationMinutes: booking.duration_minutes,
                  status: booking.status,
                })
              }
            >
              <CalendarDays /> 일정 파일 저장
            </button>
            <div className="button-row action-response-buttons">
              <button
                className="button"
                disabled={busy}
                onClick={() => void answer("confirmed")}
              >
                이 시간 괜찮아요
              </button>
              <button
                className="button button-outline"
                disabled={busy}
                onClick={() => void answer("reschedule_requested")}
              >
                시간 조정 요청
              </button>
            </div>
            <p className="muted">
              이 링크는 한 번 답변하면 만료됩니다. 조정 요청은 시간을 자동으로
              바꾸지 않습니다.
            </p>
          </>
        ) : (
          <Status kind={message.includes("전했습니다") ? "success" : "error"}>
            {message || "이미 답변했거나 만료된 링크입니다."}
          </Status>
        )}
      </section>
    </main>
  );
}

function Pricing() {
  const interest = () => {
    track("upgrade_intent", { plan: "pro", displayedPrice: 9900 });
    go("/start");
  };
  return (
    <main className="pricing-page">
      <div className="section-heading">
        <p className="eyebrow">카드 등록 없는 요금제</p>
        <h1>
          가치를 먼저 확인하고
          <br />
          필요할 때 업그레이드하세요.
        </h1>
        <p>초기 HairBridge는 예약을 받아본 뒤 비용을 선택하는 구조입니다.</p>
      </div>
      <div className="pricing-grid">
        <article className="price-card featured">
          <p>Free</p>
          <h2>0원</h2>
          <small>첫 예약부터 영구 무료 코어</small>
          <ul>
            <li>
              <Check />
              공개 예약 페이지
            </li>
            <li>
              <Check />
              계정 없는 예약 접수 무제한
            </li>
            <li>
              <Check />
              같은 시간 중복 예약 차단
            </li>
            <li>
              <Check />
              예약 상태 관리
            </li>
          </ul>
          <button className="button button-full" onClick={() => go("/start")}>
            무료로 시작
          </button>
        </article>
        <article className="price-card">
          <p>Pro 가설</p>
          <h2>예정 월 9,900원 (연 결제 기준·부가세 포함)</h2>
          <small>아직 결제되지 않습니다</small>
          <ul>
            <li>
              <Check />
              아침 일정 요약·할인 링크·월간 리포트
            </li>
            <li>
              <Check />
              예약 페이지에서 HairBridge 표시 빼기
            </li>
            <li>
              <Check />
              어디서 예약이 오는지 보기
            </li>
            <li>
              <Check />
              우선 지원
            </li>
          </ul>
          <button
            className="button button-outline button-full"
            onClick={interest}
          >
            Pro 관심 남기고 시작
          </button>
        </article>
      </div>
      <PlanPanel token={localStorage.getItem("hb_access_token")||undefined}/>
      <p className="pricing-note">
        <ShieldCheck /> 카드 등록·checkout·자동 갱신은 없습니다. 지금 제공되는
        무료 권리는 이후에도 회수하지 않습니다.
      </p>
    </main>
  );
}

function Policy({ kind }: { kind: "privacy" | "terms" }) {
  const privacy = kind === "privacy";
  return (
    <main className="policy-page">
      <article>
        <p className="eyebrow">2026년 10월 19일 시행</p>
        <h1>{privacy ? "개인정보처리방침" : "이용약관"}</h1>
        {privacy ? (
          <>
            <h2>수집하는 정보</h2>
            <p>
              디자이너 계정 운영을 위해 이름과 이메일을, 고객 예약 처리를 위해
              이름·연락처·선택 입력한 이메일·예약 시간과 요청 사항을 수집합니다. 앱인토스에서는 두피 민감도·반응 이력·최근 화학 시술 이력을 묻지 않으며 요청란에도 건강 정보를 적지 않도록 안내합니다.
            </p>
            <h2>이용 목적과 보유기간</h2>
            <p>
              정보는 예약 생성, 확인, 변경, 분쟁 대응에만 이용합니다. 고객 예약
              정보는 예약일로부터 1년 후 파기하며, 법령상 보존 의무가 있는 경우
              해당 기간만 보관합니다.
            </p>
            <h2>디자이너 장부와 공개 정보</h2>
            <p>디자이너가 등록한 고객 연락처·메모·생일·시술 기록과 비공개 고객 사진은 해당 디자이너의 고객 관리에 사용해요. 디자이너가 처리 목적을 정하고 헤어브릿지는 보관과 처리를 맡아요. 공개 작업 사진·프로필·후기는 공개 페이지에 표시돼요. 사진을 올리는 디자이너는 게시 동의를 확인해야 해요.</p>
            <h2>상담·채팅·알림·분석</h2>
            <p>예약에 연결된 상담 답변과 채팅은 예약 확인·변경·분쟁 대응을 위해 보관해요. 건강 정보는 입력하지 마세요. 선택한 디자이너에게 예약 이름·연락처·시간·요청 내용을 전달해요. 웹푸시를 허용하면 구독 endpoint와 암호화 키를 저장하고 구독 해제 시 삭제해요. 사용 분석에는 이벤트 종류와 유입 경로를 기록하며 이름·연락처·입력 원문은 보내지 않아요.</p>
            <h2>복구·지원·운영 기록</h2>
            <p>계정 비밀번호와 일회용 복구 코드는 해시로 보관하며 복구 후 기존 로그인은 만료돼요. 지원 문의·예약 신고·답변은 요청 처리와 분쟁 대응에 사용해요. 선불권·예약금·결제 수단은 디자이너의 기록 기능이며 서비스가 실제 돈을 보관하거나 결제하지 않아요. 고객 태그·마케팅 동의 기록은 해당 디자이너만 볼 수 있으며 동의 없이 홍보를 전송하지 마세요. 오래된 고객 연락처 정리는 최근 예약·진행 중 예약·법적 보존 대상이 없는 고객만 대상으로 해요.</p>
            <h2>웹 기기 저장</h2>
            <p>로그인 토큰·예약 복구 키·찜한 디자이너·선택한 알림 설정·첫 방문일·소식 읽음 상태를 기기에 저장해요. 로그아웃 또는 브라우저 저장소 삭제로 지울 수 있어요. 관리 링크는 예약 정보에 접근하는 열쇠이므로 타인에게 공유하지 마세요.</p>
            <h2>처리 위탁과 국외 이전</h2>
            <p>
              서비스 제공을 위해 Cloudflare의 Workers, D1, Pages, R2를 사용합니다. Cloudflare의 해외 인프라에서 처리될 수 있으며 서비스 제공 기간 동안 보관합니다. 이전을 원하지 않으면 이용을 중단하고 정보 삭제를 요청할 수 있습니다.
              결제와 광고 목적의 제3자 제공은 현재 활성화되어 있지 않습니다.
            </p>
            <h2>삭제 요청</h2>
            <p>
              디자이너와 고객은 본인 확인 후 정보 열람·정정·삭제를 요청할 수
              있습니다. 예약 고객은 관리 링크를 이용하고, 추가 지원은
              support@munops.com으로 요청할 수 있습니다.
            </p>
            <h2>앱인토스 기기 저장</h2>
            <p>
              앱인토스에서는 예약 관리 키·전송 전 복구 키와 사용자가 선택해
              저장한 원하는 스타일 초안을 기기에 보관합니다. 상담 화면과 예약
              상세에서 이를 직접 지울 수 있으며, 기기 저장소를 삭제하면 계정
              없는 예약을 다시 여는 정보가 사라질 수 있습니다.
            </p>
          </>
        ) : (
          <>
            <h2>서비스 범위</h2>
            <p>
              HairBridge는 실제 미용 서비스 거래의 당사자가 아니며, 디자이너의 공개 예약 페이지와 예약 관리 기능을
              제공하고, 고객에게 공개 디자이너 검색·가입 없는 예약·예약 관리를
              제공합니다. 미용 서비스의 실제 제공과 품질은 디자이너와 고객
              사이에서 정해집니다.
            </p>
            <h2>무료 이용</h2>
            <p>
              초기 Free 플랜은 카드 등록 없이 제공됩니다. 유료 기능, 자동 갱신,
              결제는 별도 고지와 명시적 동의 전에는 활성화하지 않습니다.
            </p>
            <h2>예약과 취소</h2>
            <p>
              예약 요청은 디자이너 확인 전까지 대기 상태일 수 있습니다. 고객은
              제공된 관리 링크를 통해 예약 시작 전 취소할 수 있습니다.
            </p>
            <h2>기록과 문의</h2>
            <p>예약금·선불권·마감 금액은 실제 입출금 여부를 직접 확인해 기록해 주세요. 예약 변경·취소 기준은 디자이너의 안내를 따르며, 부당한 취소나 연락 두절은 예약 관리 화면에서 신고할 수 있어요. 후기와 답변에 연락처 등 타인의 개인정보를 공개하지 마세요. 권리 침해 신고는 확인 후 숨김 등 필요한 조치를 취할 수 있어요.</p>
            <h2>금지 행위</h2>
            <p>
              타인의 정보 도용, 자동화된 대량 요청, 서비스 방해, 불법 정보
              등록을 금지합니다.
            </p>
          </>
        )}
        <p className="policy-warning">운영자: 문옵스(MUNOPS) · 문의: support@munops.com</p>
      </article>
    </main>
  );
}

function Support() {
  return (
    <main className="policy-page">
      <article>
        <p className="eyebrow">문의하기</p>
        <h1>고객지원</h1>
        <h2>문의 방법</h2>
        <p>
          예약 변경·취소는 예약 완료 뒤 받은 관리 링크에서 먼저 처리해 주세요.
          링크 오류, 계정, 개인정보 요청은 <a className="support-inline-link" href="mailto:support@munops.com">support@munops.com</a>으로
          보내 주세요. 비밀번호나 예약 관리 링크 전체는 메일에 적지 마세요.
        </p>
        <h2>응답 기준</h2>
        <p>
          접수 확인은 영업일 기준 2일 이내를 목표로 합니다. 계정 탈취·개인정보 노출처럼
          긴급한 보안 신고는 제목에 “긴급 보안”을 표시하면 우선 분류합니다.
        </p>
        <h2>개인정보 권리 요청</h2>
        <p>
          열람·정정·삭제 요청에는 본인 확인이 필요합니다. 처리 범위와 법령상 보존해야 하는
          기록은 확인 후 안내하며, 예약 관리 링크가 있는 고객은 해당 링크를 우선 사용합니다.
        </p>
        <p className="policy-warning">운영자: 문옵스(MUNOPS) · 지원 책임: owner_lane 운영자</p>
      </article>
      <SupportPanel token={localStorage.getItem("hb_access_token")||undefined}/>
    </main>
  );
}

type FieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  name: string;
};
function Field({ label, ...props }: FieldProps) {
  return (
    <label className="field">
      <span>{label}</span>
      <input {...props} />
    </label>
  );
}
function Status({
  kind,
  children,
}: {
  kind: "error" | "success";
  children: React.ReactNode;
}) {
  return (
    <div
      className={`status ${kind}`}
      role={kind === "error" ? "alert" : "status"}
    >
      {kind === "error" ? <ShieldCheck /> : <CheckCircle2 />}
      {children}
      {kind==="error"&&latestRequestId()&&<button type="button" className="text-button" onClick={()=>go(`/support?requestId=${encodeURIComponent(latestRequestId()||"")}`)}>이 문제 알리기</button>}
    </div>
  );
}
function Empty({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <div className="empty-state">
      <div className="empty-state-art" aria-hidden="true">
        <svg viewBox="0 0 112 78" focusable="false">
          <path d="M12 58C29 26 78 18 100 49" />
          <path d="M22 64C42 43 72 42 91 59" />
          <circle cx="84" cy="22" r="7" />
        </svg>
        <span>{icon}</span>
      </div>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}
function Loading({ label }: { label: string }) {
  return (
    <div className="loading" role="status">
      <span className="loading-mark">
        <BrandMark size={34} />
      </span>
      <p>{label}</p>
    </div>
  );
}
function StatusPill({ status }: { status: string }) {
  const labels: Record<string, string> = {
    pending: "확인 대기",
    confirmed: "예약 확정",
    completed: "완료",
    cancelled: "취소",
    no_show: "노쇼",
  };
  return (
    <span className={`status-pill ${status}`}>{labels[status] || status}</span>
  );
}
function formatWon(value: number) {
  return `${value.toLocaleString("ko-KR")}원`;
}
function formatDateTime(value: string) {
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    month: "long",
    day: "numeric",
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}
function formatTime(value: string) {
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

// 그룹 제목이 스크롤 밖으로 나가도 각 시간 버튼이 오전/오후를 말한다.
function formatSlotTime(value: string): string {
  const hour = Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Seoul",
      hour: "2-digit",
      hour12: false,
    }).format(new Date(value)),
  );
  const minute = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Seoul",
    minute: "2-digit",
  }).format(new Date(value));
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour < 12 ? "오전" : "오후"} ${displayHour}:${minute.padStart(2, "0")}`;
}

function slotGroups(
  values: string[],
): Array<{ label: string; values: string[] }> {
  const morning: string[] = [];
  const afternoon: string[] = [];
  for (const value of values) {
    const hour = Number(
      new Intl.DateTimeFormat("en-US", {
        timeZone: "Asia/Seoul",
        hour: "2-digit",
        hour12: false,
      }).format(new Date(value)),
    );
    (hour < 12 ? morning : afternoon).push(value);
  }
  return [
    { label: "오전", values: morning },
    { label: "오후", values: afternoon },
  ].filter((group) => group.values.length > 0);
}

function NotFound() {
  return (
    <main className="public-page">
      <section className="panel manage-card">
        <p className="eyebrow">404</p>
        <h1>페이지를 찾을 수 없어요.</h1>
        <button className="button" onClick={() => go("/")}>
          홈으로 돌아가기
        </button>
      </section>
    </main>
  );
}
function Footer({
  compact = false,
  landing = false,
}: {
  compact?: boolean;
  landing?: boolean;
}) {
  if (compact) {
    return (
      <footer className="booking-footer">
        <div>
          <span className="booking-footer-brand">
            <BrandMark size={28} />
            <BrandWordmark compact />
          </span>
          <span>
            <ShieldCheck /> 예약 정보는 담당 디자이너에게만 전달됩니다.
          </span>
        </div>
      </footer>
    );
  }
  if (landing) {
    return (
      <footer className="landing-footer">
        <span>© 2026 HairBridge</span>
        <nav aria-label="랜딩 신뢰 링크">
          <a href={`/${locale}/privacy`}>개인정보</a>
          <a href={`/${locale}/terms`}>이용약관</a>
          <a href={`/${locale}/support`}>고객지원</a>
          <a href={`${API_URL}/health`} target="_blank" rel="noreferrer">
            서비스 상태
          </a>
        </nav>
      </footer>
    );
  }
  return (
    <footer>
      <div className="footer-main">
        <div className="footer-brand">
          <button className="brand" onClick={() => go("/")}>
            <BrandMark size={42} />
            <BrandWordmark />
          </button>
          <p>독립 디자이너와 고객의 스타일, 예약, 관계를 하나로 잇습니다.</p>
        </div>
        <div className="footer-links">
          <div>
            <b>Designer</b>
            <a href={`/${locale}/start`}>디자이너 로그인</a>
            <a href={`/${locale}/pricing`}>기능과 요금</a>
          </div>
          <div>
            <b>Trust</b>
            <a href={`/${locale}/privacy`}>개인정보처리방침</a>
            <a href={`/${locale}/terms`}>이용약관</a>
            <a href={`/${locale}/support`}>고객지원</a>
            <a href={`${API_URL}/health`} target="_blank" rel="noreferrer">
              서비스 상태
            </a>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© 2026 HairBridge</span>
        <span>예약은 가볍게, 관계는 오래.</span>
      </div>
    </footer>
  );
}

export default App;
